/* Swish Court Builder configuration data.
   Keep court rules, detail metadata, limits, and default state helpers here so
   the main builder entry stays focused on runtime behavior.
*/
(function () {
  var swishcbConfigGlobal = (typeof window !== 'undefined')
    ? window
    : ((typeof globalThis !== 'undefined') ? globalThis : {});

// ---------- Constants ----------
const COURT_TYPES = Object.freeze([
  { id: 'fiba', label: 'FIBA', ruleset: 'FIBA', basketball: true },
  { id: 'nba', label: 'NBA', ruleset: 'NBA', basketball: true },
  { id: 'ncaa', label: 'NCAA', ruleset: 'NCAA', basketball: true },
  { id: 'usahs', label: 'USA High School', ruleset: 'US_HIGHSCHOOL', basketball: true },
  { id: 'tennis', label: 'Tennis', ruleset: 'TENNIS', basketball: false },
  { id: 'pickleball', label: 'Pickleball', ruleset: 'PICKLEBALL', basketball: false },
  { id: 'padel', label: 'Padel', ruleset: 'PADEL', basketball: false },
  { id: 'volleyball', label: 'Volleyball', ruleset: 'VOLLEYBALL', basketball: false },
  { id: 'shuffleboard', label: 'Shuffleboard', ruleset: 'SHUFFLEBOARD', basketball: false },
  { id: 'futsal', label: 'Futsal', ruleset: 'FUTSAL', basketball: false },
  { id: 'netball', label: 'Netball', ruleset: 'NETBALL', basketball: false }
]);

const COURT_BUILDER_LINKS = Object.freeze({
  fiba: Object.freeze({ path: '/pages/fiba-basketball-court-flooring', view: 'fiba-court-builder-temp' }),
  nba: Object.freeze({ path: '/pages/nba-basketball-court-flooring', view: 'nba-court-builder-temp' }),
  ncaa: Object.freeze({ path: '/pages/ncaa-basketball-court-flooring', view: 'ncaa-court-builder-temp' }),
  usahs: Object.freeze({ path: '/pages/high-school-basketball-court-flooring', view: 'hs-court-builder-temp' }),
  tennis: Object.freeze({ path: '/pages/tennis-flooring', view: 'tennis-court-builder' }),
  pickleball: Object.freeze({ path: '/pages/pickleball-flooring', view: 'pickleball-court-builder2' }),
  padel: Object.freeze({ path: '/pages/padel-flooring', view: 'padel-court-builder' }),
  volleyball: Object.freeze({ path: '/pages/volleyball-flooring', view: 'volleyball-court-builder' }),
  shuffleboard: Object.freeze({ path: '/pages/tennis-flooring', view: 'tennis-court-builder' }),
  futsal: Object.freeze({ path: '/pages/tennis-flooring', view: 'tennis-court-builder' }),
  netball: Object.freeze({ path: '/pages/tennis-flooring', view: 'tennis-court-builder' })
});

function buildCourtBuilderRoutes(links) {
  return Object.freeze(Object.keys(links || {}).reduce((acc, key) => {
    const link = links[key] || {};
    const path = String(link.path || '').trim();
    const view = String(link.view || '').trim();
    if (path) acc[key] = view ? path + '?view=' + view : path;
    return acc;
  }, {}));
}

const COURT_BUILDER_ROUTES = buildCourtBuilderRoutes(COURT_BUILDER_LINKS);

const COURT_TYPE_ALIASES = Object.freeze({});
function normalizeCourtTypeId(courtTypeRaw) {
  const raw = String(courtTypeRaw || '').trim().toLowerCase();
  if (!raw) return '';
  if (Object.prototype.hasOwnProperty.call(COURT_TYPE_ALIASES, raw)) {
    return COURT_TYPE_ALIASES[raw];
  }
  return raw;
}

function buildCourtTypeToRuleset(courtTypes) {
  return Object.freeze((courtTypes || []).reduce((acc, def) => {
    const id = normalizeCourtTypeId(def && def.id);
    const ruleset = String(def && def.ruleset || '').trim().toUpperCase();
    if (id && ruleset) acc[id] = ruleset;
    return acc;
  }, {}));
}

function buildRulesetToCourtType(courtTypes) {
  return Object.freeze((courtTypes || []).reduce((acc, def) => {
    const id = normalizeCourtTypeId(def && def.id);
    const ruleset = String(def && def.ruleset || '').trim().toUpperCase();
    if (id && ruleset && !acc[ruleset]) acc[ruleset] = id;
    return acc;
  }, {}));
}

const COURT_TYPE_TO_RULESET = buildCourtTypeToRuleset(COURT_TYPES);
const RULESET_TO_COURT_TYPE = buildRulesetToCourtType(COURT_TYPES);
const BASKETBALL_RULESETS = new Set(COURT_TYPES.filter(def => !!(def && def.basketball)).map(def => String(def.ruleset || '').toUpperCase()));
const NON_BASKETBALL_UI_LOCK_COURT_TYPES = new Set(COURT_TYPES.filter(def => !(def && def.basketball)).map(def => normalizeCourtTypeId(def && def.id)));
const BASKETBALL_EXTRA_LINE_IDS = Object.freeze(['fiba3', 'nba3', 'ncaa3', 'usahs3']);

function freezeOfficialGeometry(def) {
  return Object.freeze(Object.assign({}, def || {}));
}

const OFFICIAL_GEOMETRY = Object.freeze({
  FIBA: freezeOfficialGeometry({
    line: 0.05,
    keyW: 4.9,
    keyL: 5.8,
    freeThrowLineLength: 3.6,
    centerLineExtension: 0.15,
    centerR: 1.8,
    ftR: 1.8,
    restrictedR: 1.275,
    restrictedOuterR: 1.3,
    threeR: 6.75,
    threeSideOffset: 0.9,
    hoopToEnd: 1.2,
    rimFromBackboard: 0.375,
    equipment: Object.freeze({
      backboardWidthM: 1.8,
      backboardHeightM: 1.05,
      backboardLineWidthM: 0.05,
      backboardTargetWidthM: 0.59,
      backboardTargetHeightM: 0.45,
      rimHeightM: 3.05,
      rimHeightToleranceM: 0.006,
      rimInsideDiameterMinM: 0.45,
      rimInsideDiameterMaxM: 0.459,
      rimNearestInsideEdgeFromBackboardM: 0.151,
      netLengthMinM: 0.4,
      netLengthMaxM: 0.45
    }),
    measurementBasis: 'outer-edge',
    source: 'FIBA Official Basketball Rules 2024'
  }),
  NBA: freezeOfficialGeometry({
    line: 0.0508,
    keyW: 4.8768,
    keyL: 5.7912,
    freeThrowLineLength: 3.6576,
    centerR: 1.8288,
    ftR: 1.8288,
    restrictedR: 1.2192,
    threeR: 7.239,
    threeSideOffset: 0.9144,
    hoopToEnd: 1.2192,
    rimFromBackboard: 0.381,
    inboundHashFromBaseline: 8.5344,
    inboundHashLength: 0.9144,
    equipment: Object.freeze({
      backboardWidthM: 1.8288,
      backboardHeightM: 1.0668,
      backboardTargetWidthM: 0.6096,
      backboardTargetHeightM: 0.4572,
      backboardTargetLineWidthM: 0.0508,
      rimHeightM: 3.048,
      rimInsideDiameterM: 0.4572,
      rimNearestInsideEdgeFromBackboardM: 0.1524,
      netLengthM: 0.4572
    }),
    measurementBasis: 'outside-edge',
    source: 'NBA Rule No. 1 Court Dimensions, 2025-26'
  }),
  NCAA: freezeOfficialGeometry({
    line: 0.0508,
    keyW: 3.6576,
    keyL: 5.7912,
    freeThrowLineLength: 3.6576,
    centerR: 1.8288,
    ftR: 1.8288,
    restrictedR: 1.2192,
    restrictedBasis: 'inner-edge',
    restrictedAppliesTo: 'men',
    threeR: 6.75,
    threeCornerR: 6.6,
    threeSideOffset: 1.02,
    hoopToEnd: 1.2192,
    rimFromBackboard: 0.381,
    equipment: Object.freeze({
      backboardWidthM: 1.8288,
      backboardHeightM: 1.0668,
      rimHeightM: 3.048,
      rimInsideDiameterM: 0.4572,
      rimNearestInsideEdgeFromBackboardM: 0.1524
    }),
    measurementBasis: 'outside-edge',
    source: 'NCAA 2025-26 Basketball Court Diagram'
  }),
  US_HIGHSCHOOL: freezeOfficialGeometry({
    line: 0.0508,
    keyW: 3.6576,
    keyL: 5.7912,
    freeThrowLineLength: 3.6576,
    centerR: 1.8288,
    centerInnerR: 0.6096,
    ftR: 1.8288,
    restrictedR: 0,
    threeR: 6.0198,
    threeSideOffset: 1.6002,
    hoopToEnd: 1.2192,
    rimFromBackboard: 0.381,
    equipment: Object.freeze({
      backboardWidthM: 1.8288,
      rimHeightM: 3.048,
      rimInsideDiameterM: 0.4572,
      rimNearestInsideEdgeFromBackboardM: 0.1524
    }),
    measurementBasis: 'outside-edge',
    source: 'NFHS Basketball Court Diagram, 2024'
  }),
  TENNIS: freezeOfficialGeometry({
    line: 0.05,
    playableLength: 23.77,
    playableWidth: 10.97,
    singlesWidth: 8.23,
    serviceLineFromNet: 6.4,
    centerMarkLength: 0.1,
    netPostOffsetFromDoublesSideline: 0.9144,
    netHeightAtPosts: 1.07,
    netHeightAtCenter: 0.914,
    netPostMaxDiameter: 0.15,
    singlesStickOffsetFromSinglesSideline: 0.9144,
    singlesStickMaxDiameter: 0.075,
    keyW: 4.115,
    keyL: 6.4,
    centerR: 0,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 1.575,
    measurementBasis: 'outside-edge',
    source: 'ITF Rules of Tennis 2026'
  }),
  PICKLEBALL: freezeOfficialGeometry({
    line: 0.0508,
    playableLength: 13.4112,
    playableWidth: 6.096,
    nonVolleyZone: 2.1336,
    netPostInsideDistance: 6.7056,
    netPostInsideDistanceTolerance: 0.0254,
    netPostMaxDiameter: 0.0762,
    netLengthMin: 6.6294,
    netHeightAtSidelines: 0.9144,
    netHeightAtCenter: 0.8636,
    netHeightTolerance: 0.00635,
    keyW: 0,
    keyL: 0,
    centerR: 0,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 0,
    measurementBasis: 'outside-edge',
    source: 'USA Pickleball Official Rulebook 2026'
  }),
  PADEL: freezeOfficialGeometry({
    line: 0.05,
    playableLength: 20,
    playableWidth: 10,
    serviceLineFromNet: 6.95,
    serviceLineFromBackWall: 3.05,
    centerServiceExtension: 0.2,
    netHeightAtCenter: 0.88,
    netHeightAtEnds: 0.92,
    netHeightTolerance: 0.005,
    netPostMaxHeight: 1.05,
    netCableMaxDiameter: 0.01,
    keyW: 0,
    keyL: 0,
    centerR: 0,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 0,
    measurementBasis: 'centerline',
    source: 'FIP Rules of Padel, effective 2026-01-01'
  }),
  VOLLEYBALL: freezeOfficialGeometry({
    line: 0.05,
    playableLength: 18,
    playableWidth: 9,
    attackLineFromCenter: 3,
    attackExtensionDashLength: 0.15,
    attackExtensionGap: 0.2,
    attackExtensionTotal: 1.75,
    attackExtensionCount: 5,
    serviceZoneMarkLength: 0.15,
    serviceZoneMarkGapFromEndline: 0.2,
    netHeightMen: 2.43,
    netHeightWomen: 2.24,
    keyW: 0,
    keyL: 0,
    centerR: 0,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 0,
    measurementBasis: 'inside-boundary',
    source: 'FIVB Volleyball Rules 2025-2028'
  }),
  SHUFFLEBOARD: freezeOfficialGeometry({
    line: 0.0254,
    playableLength: 15.8496,
    playableWidth: 1.8288,
    baseLine: 1.9812,
    tenOffDepth: 0.4572,
    scoringBand: 0.9144,
    triangleDepth: 2.7432,
    deadLineGap: 0.9144,
    tenOffSideInset: 0.1524,
    tenOffSeparatorHalfBase: 0.0381,
    keyW: 0,
    keyL: 0,
    centerR: 0,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 0,
    measurementBasis: 'centerline',
    source: 'National Shuffleboard Association Rules, revised January 2025'
  }),
  CORNHOLE: freezeOfficialGeometry({
    line: 0.05,
    playableLength: 13.716,
    playableWidth: 3.048,
    boardLength: 1.2192,
    boardWidth: 0.6096,
    boardFrontGap: 8.2296,
    pitcherBoxLength: 1.2192,
    pitcherBoxWidth: 0.9144,
    endClearance: 1.524,
    sideClearance: 0.3048,
    holeDiameter: 0.1524,
    holeCenterFromBack: 0.2286,
    holeCenterFromSide: 0.3048,
    keyW: 0,
    keyL: 0,
    centerR: 0,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 0,
    measurementBasis: 'court-layout',
    source: 'ACO Official Rules and ACA Official Cornhole Rules'
  }),
  FUTSAL: freezeOfficialGeometry({
    line: 0.08,
    playableLength: 40,
    playableWidth: 20,
    goalWidth: 3,
    goalHeight: 2,
    goalPostWidth: 0.08,
    goalOutsideWidth: 3.16,
    penaltySpan: 15.16,
    penaltyDepth: 6,
    penaltyMark: 6,
    secondPenaltyMark: 10,
    penaltyMarkRadius: 0.06,
    goalkeeperMarkFromSecond: 5,
    goalkeeperMarkLength: 0.4,
    secondPenaltySideMarkOffset: 5,
    secondPenaltySideMarkRadius: 0.04,
    substitutionMarkDistanceFromHalfway: 5,
    substitutionZoneLength: 5,
    substitutionMarkLength: 0.8,
    substitutionTouchline: 'top',
    cornerR: 0.25,
    cornerDistanceMarkDistance: 5,
    cornerDistanceMarkGapFromGoalLine: 0.05,
    cornerDistanceMarkLength: 0.4,
    keyW: 15.16,
    keyL: 6,
    centerR: 3,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 0,
    measurementBasis: 'outside-goalpost',
    source: 'FIFA Futsal Laws of the Game 2025-2026'
  }),
  NETBALL: freezeOfficialGeometry({
    line: 0.05,
    playableLength: 30.5,
    playableWidth: 15.25,
    goalCircleR: 4.9,
    centerCircleR: 0.45,
    goalPostMarkerR: 0.1,
    goalPostPoleDiameterMin: 0.065,
    goalPostPoleDiameterMax: 0.1,
    goalPostPaddingMax: 0.05,
    goalPostHeight: 3.05,
    goalRingInternalDiameter: 0.38,
    goalRingRodDiameter: 0.015,
    goalRingBarLength: 0.15,
    keyW: 4.9,
    keyL: 4.9,
    centerR: 0.45,
    ftR: 0,
    threeR: 0,
    hoopToEnd: 0,
    measurementBasis: 'line-included',
    source: 'World Netball Rules Book 2024'
  })
});

const OFFICIAL_SOURCE_URLS = Object.freeze({
  FIBA: 'https://assets.fiba.basketball/image/upload/documents-corporate-fiba-official-rules-2024-v10a.pdf',
  NBA: 'https://official.nba.com/rule-no-1-court-dimensions-equipment/',
  NCAA: 'https://ncaaorg.s3.amazonaws.com/championships/sports/basketball/rules/common/PRXBB_CourtDiagram.pdf',
  US_HIGHSCHOOL: 'https://assets.nfhs.org/umbraco/media/7213108/basketball-court-diagram.pdf',
  TENNIS: 'https://www.itftennis.com/en/about-us/governance/rules-and-regulations/',
  PICKLEBALL: 'https://usapickleball.org/docs/rules/USAP-Official-Rulebook.pdf',
  PADEL: 'https://www.padelfip.com/wp-content/uploads/2025/12/FIP_Rules-of-Padel.pdf',
  VOLLEYBALL: 'https://www.fivb.com/wp-content/uploads/2025/01/FIVB-Volleyball_Rules2025_2028-EN-v05.pdf',
  SHUFFLEBOARD: 'https://www.maplefloor.org/uploads/block-files/695ec71186e97_NSARulesRevisedJan2025.pdf',
  CORNHOLE: 'https://americancornhole.com/rules/',
  FUTSAL: 'https://digitalhub.fifa.com/m/20d52e6779b53321/original/FUTSAL-Laws-of-the-Game-2025-2026.pdf',
  NETBALL: 'https://netball.sport/wp-content/uploads/2024/01/World-Netball-Rules-Book-2024.pdf'
});

const SWISHCB_INCH_M = 0.0254;
const SWISHCB_FOOT_M = 0.3048;
const HOOP_PRESET_SOURCE_URLS = Object.freeze({
  goalrillaOverhangGuide: 'https://www.goalrilla.com/blogs/news/how-overhang-determines-your-installation-location',
  goalrillaChart10Ft: 'https://cdn.shopify.com/s/files/1/0490/5710/4037/files/MicrosoftTeams-image_45.jpg?v=1701190903',
  goaliathChart10Ft: 'https://cdn.shopify.com/s/files/1/0490/5710/4037/files/MicrosoftTeams-image_44_c8a52e10-5b82-4846-870f-adba70da58d5.jpg?v=1701190722',
  fixedHeight: 'https://www.recunlimited.com/p/72-fixed-height-in-ground-basketball-hoop-with-glass-backboard/'
});

function freezeHoopPresetSpec(def) {
  const overhangIn = Math.max(0, Number(def && def.overhangIn) || 0);
  const manufacturerOverhangM = overhangIn * SWISHCB_INCH_M;
  return Object.freeze(Object.assign({
    manufacturerOverhangIn: overhangIn,
    manufacturerOverhangM,
    manufacturerOverhangCm: manufacturerOverhangM * 100,
    manufacturerOverhangFt: overhangIn / 12,
    manufacturerOverhangBasis: 'pole-front-face-to-backboard-front-face',
    rimHeightIn: 120,
    rimHeightM: 10 * SWISHCB_FOOT_M,
    rimHeightBasis: '10 ft rim height chart',
    backboardFromEndlineDefaultM: manufacturerOverhangM,
    rimCenterFromBackboardDefaultM: 0.381,
    sourceUrls: Object.freeze([])
  }, def || {}, {
    manufacturerOverhangIn: overhangIn,
    manufacturerOverhangM,
    manufacturerOverhangCm: manufacturerOverhangM * 100,
    manufacturerOverhangFt: overhangIn / 12,
    sourceUrls: Object.freeze((def && def.sourceUrls) || [])
  }));
}

const HOOP_PRESET_SPECS = Object.freeze({
  DC72E1: freezeHoopPresetSpec({ id: 'DC72E1', aliases: Object.freeze(['DC72']), brand: 'Goalrilla', model: 'DC72E1 / DC72', overhangIn: 50, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  CV72: freezeHoopPresetSpec({ id: 'CV72', brand: 'Goalrilla', model: 'CV72', overhangIn: 46.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  CV60: freezeHoopPresetSpec({ id: 'CV60', brand: 'Goalrilla', model: 'CV60', overhangIn: 30.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  CV54: freezeHoopPresetSpec({ id: 'CV54', brand: 'Goalrilla', model: 'CV54', overhangIn: 30, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  FT72: freezeHoopPresetSpec({ id: 'FT72', brand: 'Goalrilla', model: 'FT72', overhangIn: 38.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  FT60: freezeHoopPresetSpec({ id: 'FT60', brand: 'Goalrilla', model: 'FT60', overhangIn: 30.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  FT54: freezeHoopPresetSpec({ id: 'FT54', brand: 'Goalrilla', model: 'FT54', overhangIn: 24.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  GS72C: freezeHoopPresetSpec({ id: 'GS72C', brand: 'Goalrilla', model: 'GS72C', overhangIn: 33.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  GS60C: freezeHoopPresetSpec({ id: 'GS60C', brand: 'Goalrilla', model: 'GS60C', overhangIn: 26, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  GS54C: freezeHoopPresetSpec({ id: 'GS54C', brand: 'Goalrilla', model: 'GS54C', overhangIn: 22.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goalrillaChart10Ft]) }),
  FIXEDHEIGHT: freezeHoopPresetSpec({ id: 'FIXEDHEIGHT', brand: 'Goalrilla', model: 'Fixed Height', overhangIn: 60, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.fixedHeight]) }),
  GB60: freezeHoopPresetSpec({ id: 'GB60', brand: 'Goaliath', model: 'GB60 / Ignite', overhangIn: 26, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goaliathChart10Ft]) }),
  GB54: freezeHoopPresetSpec({ id: 'GB54', brand: 'Goaliath', model: 'GB54 / Prodigy', overhangIn: 26, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goaliathChart10Ft]) }),
  GB50: freezeHoopPresetSpec({ id: 'GB50', brand: 'Goaliath', model: 'GB50 / Warrior', overhangIn: 19, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goaliathChart10Ft]) }),
  GOTEK54IG: freezeHoopPresetSpec({ id: 'GOTEK54IG', brand: 'Goaliath', model: 'GoTek54 In-Ground', overhangIn: 24.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goaliathChart10Ft]) }),
  GOTEK54P: freezeHoopPresetSpec({ id: 'GOTEK54P', brand: 'Goaliath', model: 'GoTek54 Portable', overhangIn: 24.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goaliathChart10Ft]) }),
  GOTEK50P: freezeHoopPresetSpec({ id: 'GOTEK50P', brand: 'Goaliath', model: 'GoTek50 Portable', overhangIn: 19.5, sourceUrls: Object.freeze([HOOP_PRESET_SOURCE_URLS.goalrillaOverhangGuide, HOOP_PRESET_SOURCE_URLS.goaliathChart10Ft]) })
});

const HOOP_PRESET_ALIASES = Object.freeze({
  DC72: 'DC72E1',
  DC72E1: 'DC72E1',
  GS72C: 'GS72C',
  GS60C: 'GS60C',
  GS54C: 'GS54C',
  GOALRILLADC72: 'DC72E1'
});

function normalizeHoopPresetId(value) {
  const raw = String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!raw) return '';
  return HOOP_PRESET_ALIASES[raw] || raw;
}

function cloneHoopPresetSpec(presetId) {
  const key = normalizeHoopPresetId(presetId);
  return Object.assign({}, HOOP_PRESET_SPECS[key] || {});
}

function resolveHoopPresetGeometry(presetId, options = {}) {
  const key = normalizeHoopPresetId(presetId);
  const spec = HOOP_PRESET_SPECS[key];
  if (!spec) return null;
  const selectedBackboardM = Number.isFinite(Number(options.backboardFromEndlineM))
    ? Math.max(0, Number(options.backboardFromEndlineM))
    : spec.manufacturerOverhangM;
  const rimCenterFromBackboardM = Number.isFinite(Number(options.rimCenterFromBackboardM))
    ? Math.max(0, Number(options.rimCenterFromBackboardM))
    : Math.max(0, Number(spec.rimCenterFromBackboardDefaultM) || 0.381);
  return Object.freeze(Object.assign({}, spec, {
    presetId: key,
    requestedPresetId: String(presetId || ''),
    manufacturerOverhangM: spec.manufacturerOverhangM,
    manufacturerOverhangCm: spec.manufacturerOverhangCm,
    manufacturerOverhangFt: spec.manufacturerOverhangFt,
    backboardFromEndlineM: selectedBackboardM,
    backboardFromEndlineBasis: 'selected-installation-anchor',
    rimCenterFromBackboardM,
    rimCenterFromEndlineM: selectedBackboardM + rimCenterFromBackboardM,
    poleFrontFaceFromEndlineM: selectedBackboardM - spec.manufacturerOverhangM
  }));
}

const OFFICIAL_GEOMETRY_ALIASES = Object.freeze({
  USHS: 'US_HIGHSCHOOL',
  USAHS: 'US_HIGHSCHOOL',
  HS: 'US_HIGHSCHOOL',
  NFHS: 'US_HIGHSCHOOL'
});

function normalizeOfficialGeometryId(id) {
  const key = String(id || '').trim().toUpperCase();
  return OFFICIAL_GEOMETRY_ALIASES[key] || key;
}

function cloneOfficialGeometry(id) {
  const key = normalizeOfficialGeometryId(id);
  return Object.assign({}, OFFICIAL_GEOMETRY[key] || {});
}

function resolveRulesetIdForOfficialGeometry(rulesOrId) {
  if (rulesOrId && typeof rulesOrId === 'object') {
    const id = normalizeOfficialGeometryId(rulesOrId.id);
    if (id) return id;
  }
  const raw = String(rulesOrId || '').trim();
  const upper = normalizeOfficialGeometryId(raw);
  if (OFFICIAL_GEOMETRY[upper]) return upper;
  const courtMapped = String(COURT_TYPE_TO_RULESET[normalizeCourtTypeId(raw)] || '').trim().toUpperCase();
  if (OFFICIAL_GEOMETRY[courtMapped]) return courtMapped;
  const overlay = (typeof EXTRA_LINE_TYPES !== 'undefined' && Array.isArray(EXTRA_LINE_TYPES))
    ? EXTRA_LINE_TYPES.find(def => String(def && def.id || '').toLowerCase() === raw.toLowerCase())
    : null;
  const overlayRuleset = String(overlay && overlay.ruleset || '').trim().toUpperCase();
  return OFFICIAL_GEOMETRY[overlayRuleset] ? overlayRuleset : upper;
}

function officialClampM(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function officialPositiveM(value, fallback) {
  return Math.max(0, officialClampM(value, fallback));
}

function officialBasketballLaneMarks(rulesetId, lineM) {
  const line = Math.max(0, Number(lineM) || 0);
  const mark = (offsetM, projectionM, widthM, role) => Object.freeze({
    offsetM: Math.max(0, Number(offsetM) || 0),
    projectionM: Math.max(0, Number(projectionM) || 0),
    lenM: Math.max(0, Number(projectionM) || 0) + (line * 0.5),
    widthM: Math.max(line, Number(widthM) || line),
    role: role || 'lane-space-mark',
    offsetBasis: 'baseline',
    projectionBasis: 'outside-lane-edge'
  });
  if (rulesetId === 'FIBA') {
    return Object.freeze([
      mark(1.775, 0.1),
      mark(2.85, 0.1, 0.4, 'neutral-zone'),
      mark(3.925, 0.1),
      mark(4.825, 0.1)
    ]);
  }
  if (rulesetId === 'NBA') {
    return Object.freeze([
      mark(1.2192, 0.1524),
      mark(2.286, 0.1524, 0.3048, 'neutral-zone'),
      mark(3.3528, 0.1524),
      mark(4.2672, 0.1524)
    ]);
  }
  if (rulesetId === 'NCAA') {
    return Object.freeze([
      mark(1.2192, 0.2032),
      mark(2.286, 0.2032, 0.3048, 'neutral-zone'),
      mark(3.3528, 0.2032),
      mark(4.2672, 0.2032),
      mark(5.1816, 0.2032)
    ]);
  }
  if (rulesetId === 'US_HIGHSCHOOL') {
    return Object.freeze([
      mark(2.413, 0.2032),
      mark(3.3274, 0.2032),
      mark(4.2418, 0.2032),
      mark(5.1562, 0.2032)
    ]);
  }
  return Object.freeze([]);
}

function resolveOfficialBasketballLaneMarks(rulesOrId, options = {}) {
  const rulesetId = resolveRulesetIdForOfficialGeometry(rulesOrId);
  if (!BASKETBALL_RULESETS.has(rulesetId)) return Object.freeze([]);
  const rules = (rulesOrId && typeof rulesOrId === 'object') ? rulesOrId : RULESETS[rulesetId];
  const lineM = Math.max(
    0,
    Number(options.lineM) ||
      Number(rules && rules.geometry && rules.geometry.line) ||
      Number(OFFICIAL_GEOMETRY[rulesetId] && OFFICIAL_GEOMETRY[rulesetId].line) ||
      0
  );
  return officialBasketballLaneMarks(rulesetId, lineM);
}

function officialBasketballAuxiliaryMarks(rulesetId, official, lineM) {
  const line = Math.max(0, Number(lineM) || 0);
  const keyW = officialPositiveM(official && official.keyW, 0);
  const widthM = officialPositiveM(official && official.playableWidth, 15);
  const lengthM = officialPositiveM(official && official.playableLength, 28);
  const keyTopOuter = (widthM - keyW) * 0.5;
  const keyBottomOuter = keyTopOuter + keyW;
  const mark = (def) => Object.freeze(Object.assign({ lineWidthM: line }, def || {}));
  if (rulesetId === 'FIBA') {
    return Object.freeze([
      mark({
        id: 'fibaThrowInLineTop',
        kind: 'line',
        basis: 'outer-edge-from-endline',
        x1M: 8.325 - (line * 0.5),
        y1M: -0.15,
        x2M: 8.325 - (line * 0.5),
        y2M: 0,
        lengthM: 0.15,
        note: 'One of four official throw-in lines; mirrored left/right by side and paired top/bottom.'
      }),
      mark({
        id: 'fibaThrowInLineBottom',
        kind: 'line',
        basis: 'outer-edge-from-endline',
        x1M: 8.325 - (line * 0.5),
        y1M: widthM + 0.15,
        x2M: 8.325 - (line * 0.5),
        y2M: widthM,
        lengthM: 0.15,
        note: 'One of four official throw-in lines; mirrored left/right by side and paired top/bottom.'
      })
    ]);
  }
  if (rulesetId === 'NBA') {
    const sidelineHashX = officialPositiveM(official && official.inboundHashFromBaseline, 8.5344);
    const sidelineHashLen = officialPositiveM(official && official.inboundHashLength, 0.9144);
    const baselineHashOffset = 0.9144;
    const baselineHashLen = 0.1524;
    const substitutionFromCenter = 1.2192;
    const substitutionLen = 0.9144;
    return Object.freeze([
      mark({ id: 'nbaSidelineHashTop', kind: 'line', x1M: sidelineHashX, y1M: 0, x2M: sidelineHashX, y2M: sidelineHashLen, basis: 'centerline' }),
      mark({ id: 'nbaSidelineHashBottom', kind: 'line', x1M: sidelineHashX, y1M: widthM, x2M: sidelineHashX, y2M: widthM - sidelineHashLen, basis: 'centerline' }),
      mark({ id: 'nbaBaselineLaneHashUpper', kind: 'line', x1M: 0, y1M: keyTopOuter - baselineHashOffset, x2M: baselineHashLen, y2M: keyTopOuter - baselineHashOffset, basis: 'centerline' }),
      mark({ id: 'nbaBaselineLaneHashLower', kind: 'line', x1M: 0, y1M: keyBottomOuter + baselineHashOffset, x2M: baselineHashLen, y2M: keyBottomOuter + baselineHashOffset, basis: 'centerline' }),
      mark({ id: 'nbaSubstitutionBoxLeft', kind: 'line', x1M: (lengthM * 0.5) - substitutionFromCenter, y1M: 0, x2M: (lengthM * 0.5) - substitutionFromCenter, y2M: substitutionLen, basis: 'centerline' }),
      mark({ id: 'nbaSubstitutionBoxRight', kind: 'line', x1M: (lengthM * 0.5) + substitutionFromCenter, y1M: 0, x2M: (lengthM * 0.5) + substitutionFromCenter, y2M: substitutionLen, basis: 'centerline' })
    ]);
  }
  return Object.freeze([]);
}

function resolveOfficialCourtGeometry(rulesOrId, options = {}) {
  const rulesetId = resolveRulesetIdForOfficialGeometry(rulesOrId);
  const rules = (rulesOrId && typeof rulesOrId === 'object') ? rulesOrId : RULESETS[rulesetId];
  const official = Object.assign({}, cloneOfficialGeometry(rulesetId), (rules && rules.geometry) || {});
  const officialLengthM = Math.max(0.001, Number(official.playableLength) || Number(rules && rules.playableLength) || Number(options.surfaceLengthM) || 0.001);
  const officialWidthM = Math.max(0.001, Number(official.playableWidth) || Number(rules && rules.playableWidth) || Number(options.surfaceWidthM) || 0.001);
  const surfaceLengthM = Math.max(0.001, Number(options.surfaceLengthM) || officialLengthM);
  const surfaceWidthM = Math.max(0.001, Number(options.surfaceWidthM) || officialWidthM);
  const lineM = Math.max(0, Number(official.line) || 0);
  const isBasketball = BASKETBALL_RULESETS.has(rulesetId);
  const fitsLength = surfaceLengthM >= (officialLengthM - 1e-9);
  const fitsWidth = surfaceWidthM >= (officialWidthM - 1e-9);
  const requestedPlayableRectM = options && options.playableRectM && typeof options.playableRectM === 'object'
    ? options.playableRectM
    : null;
  const hasRequestedPlayableRect = !!(
    requestedPlayableRectM &&
    Number.isFinite(Number(requestedPlayableRectM.w)) &&
    Number.isFinite(Number(requestedPlayableRectM.h)) &&
    Number(requestedPlayableRectM.w) > 0 &&
    Number(requestedPlayableRectM.h) > 0
  );
  let scale = isBasketball
    ? 1
    : (fitsLength && fitsWidth ? 1 : Math.max(0.001, Math.min(surfaceLengthM / officialLengthM, surfaceWidthM / officialWidthM)));
  let rectW = officialLengthM * scale;
  let rectH = officialWidthM * scale;
  let playableRectM;
  if (hasRequestedPlayableRect) {
    rectW = Math.max(0.001, Math.min(surfaceLengthM, Number(requestedPlayableRectM.w) || rectW));
    rectH = Math.max(0.001, Math.min(surfaceWidthM, Number(requestedPlayableRectM.h) || rectH));
    scale = isBasketball
      ? 1
      : Math.max(0.001, Math.min(rectW / officialLengthM, rectH / officialWidthM));
    playableRectM = {
      x: Math.max(0, Math.min(Math.max(0, surfaceLengthM - rectW), Number(requestedPlayableRectM.x) || 0)),
      y: Math.max(0, Math.min(Math.max(0, surfaceWidthM - rectH), Number(requestedPlayableRectM.y) || 0)),
      w: rectW,
      h: rectH
    };
  } else {
    playableRectM = {
      x: Math.max(0, (surfaceLengthM - rectW) * 0.5),
      y: Math.max(0, (surfaceWidthM - rectH) * 0.5),
      w: rectW,
      h: rectH
    };
  }
  const visibleRectM = isBasketball
    ? {
      x: 0,
      y: 0,
      w: Math.min(surfaceLengthM, officialLengthM),
      h: Math.min(surfaceWidthM, officialWidthM)
    }
    : Object.assign({}, playableRectM);
  const transformMode = isBasketball
    ? ((fitsLength && fitsWidth) ? 'official-scale' : 'cropped')
    : (hasRequestedPlayableRect
      ? (scale >= 1 - 1e-9 ? 'official-scale' : 'miniature')
      : ((fitsLength && fitsWidth) ? 'official-scale' : 'miniature'));
  const centerlineFromOuterRadius = (radiusM) => Math.max(0, officialPositiveM(radiusM, 0) - (lineM * 0.5));
  const centerlineFromOuterDistance = (distanceM) => Math.max(0, officialPositiveM(distanceM, 0) - (lineM * 0.5));
  const centerlineFromInsideOffset = (offsetM) => Math.max(0, officialPositiveM(offsetM, 0) + (lineM * 0.5));
  const features = Object.create(null);
  const objects = Object.create(null);
  const drawPrimitives = [];
  function addPrimitive(primitive) {
    if (!primitive || typeof primitive !== 'object') return null;
    const copy = Object.assign({
      rulesetId,
      lineWidthM: lineM,
      basis: primitive.basis || official.measurementBasis || null
    }, primitive);
    drawPrimitives.push(Object.freeze(copy));
    return copy;
  }
  function addLinePrimitive(id, x1M, y1M, x2M, y2M, extra = {}) {
    return addPrimitive(Object.assign({ id, kind: 'line', x1M, y1M, x2M, y2M }, extra));
  }
  function addRectPrimitive(id, xM, yM, wM, hM, extra = {}) {
    return addPrimitive(Object.assign({ id, kind: 'rect', xM, yM, wM, hM }, extra));
  }
  function addCirclePrimitive(id, xM, yM, rM, extra = {}) {
    return addPrimitive(Object.assign({ id, kind: 'circle', xM, yM, rM }, extra));
  }
  function addArcPrimitive(id, cxM, cyM, rM, extra = {}) {
    return addPrimitive(Object.assign({ id, kind: 'arc', cxM, cyM, rM }, extra));
  }
  function addPointPrimitive(id, xM, yM, rM, extra = {}) {
    return addPrimitive(Object.assign({ id, kind: 'point', xM, yM, rM: Math.max(lineM, Number(rM) || lineM) }, extra));
  }
  function addBoundaryPrimitives(prefix, sport, lengthM, widthM, labels = {}) {
    addLinePrimitive(prefix + 'SidelineTop', 0, 0, lengthM, 0, { sport, role: labels.sideline || 'sideline', basis: 'inside-edge' });
    addLinePrimitive(prefix + 'SidelineBottom', 0, widthM, lengthM, widthM, { sport, role: labels.sideline || 'sideline', basis: 'inside-edge' });
    addLinePrimitive(prefix + 'EndlineLeft', 0, 0, 0, widthM, { sport, role: labels.endline || 'endline', basis: 'inside-edge' });
    addLinePrimitive(prefix + 'EndlineRight', lengthM, 0, lengthM, widthM, { sport, role: labels.endline || 'endline', basis: 'inside-edge' });
  }
  function mirrorXFromBaseline(side, distanceM) {
    const d = Number(distanceM) || 0;
    return side === 'right' ? officialLengthM - d : d;
  }
  function mapBasketballAuxMark(side, mark) {
    const m = Object.assign({}, mark || {});
    ['x1M', 'x2M', 'xM', 'cxM'].forEach((key) => {
      if (Number.isFinite(Number(m[key]))) m[key] = mirrorXFromBaseline(side, Number(m[key]));
    });
    m.id = side + '-' + String(m.id || 'officialAuxiliaryMark');
    m.side = side;
    return m;
  }
  function readHoopAnchorOption(side, fallbackM) {
    const s = String(side || '').trim().toLowerCase();
    const camel = s ? (s.charAt(0).toUpperCase() + s.slice(1)) : '';
    const candidates = [];
    if (options && typeof options === 'object') {
      candidates.push(options[s + 'BackboardFromEndlineM']);
      candidates.push(options['hoopOverhang' + camel + 'M']);
      candidates.push(options['backboardFromEndline' + camel + 'M']);
      if (options.hoopAnchors && typeof options.hoopAnchors === 'object') {
        candidates.push(options.hoopAnchors[s + 'M']);
        candidates.push(options.hoopAnchors[s]);
        candidates.push(options.hoopAnchors[s + 'BackboardFromEndlineM']);
      }
      if (options.hoopOverhangs && typeof options.hoopOverhangs === 'object') {
        candidates.push(options.hoopOverhangs[s + 'M']);
        candidates.push(options.hoopOverhangs[s]);
      }
      candidates.push(options.backboardFromEndlineM);
      candidates.push(options.hoopOverhangM);
    }
    for (let i = 0; i < candidates.length; i += 1) {
      const n = Number(candidates[i]);
      if (Number.isFinite(n)) return Math.max(0, n);
    }
    return Math.max(0, Number(fallbackM) || 0);
  }

  if (isBasketball) {
    const keyWOuterM = officialPositiveM(official.keyW, 0);
    const keyLOuterM = officialPositiveM(official.keyL, 0);
    const centerLineExtensionM = officialPositiveM(official.centerLineExtension, 0);
    const officialBackboardFromEndlineM = officialPositiveM(official.hoopToEnd, 0);
    const rimCenterFromBackboardM = officialPositiveM(official.rimFromBackboard, 0);
    const freeThrowFarEdgeFromBackboardM = Math.max(0, keyLOuterM - officialBackboardFromEndlineM);
    const freeThrowStrokeCenterFromBackboardM = Math.max(0, freeThrowFarEdgeFromBackboardM - (lineM * 0.5));
    const keyTopOuterM = (officialWidthM - keyWOuterM) * 0.5;
    const keyBottomOuterM = keyTopOuterM + keyWOuterM;
    const centerR = centerlineFromOuterRadius(official.centerR);
    const centerInnerR = officialPositiveM(official.centerInnerR, 0) > 0
      ? centerlineFromOuterRadius(official.centerInnerR)
      : 0;
    const restrictedOuter = Number.isFinite(Number(official.restrictedOuterR))
      ? Number(official.restrictedOuterR)
      : NaN;
    const restrictedBasis = String(official.restrictedBasis || '').trim().toLowerCase();
    const restrictedR = Number.isFinite(restrictedOuter)
      ? centerlineFromOuterRadius(restrictedOuter)
      : (restrictedBasis === 'inner-edge'
        ? centerlineFromInsideOffset(official.restrictedR)
        : officialPositiveM(official.restrictedR, 0));
    const laneMarks = officialBasketballLaneMarks(rulesetId, lineM);
    const auxiliaryMarks = officialBasketballAuxiliaryMarks(rulesetId, Object.assign({
      playableLength: officialLengthM,
      playableWidth: officialWidthM
    }, official), lineM);
    const buildBasketballDraw = (backboardFromEndlineM) => {
      const backboardM = Math.max(0, Number(backboardFromEndlineM) || 0);
      const deltaM = backboardM - officialBackboardFromEndlineM;
      const keyFillLengthM = Math.max(0, backboardM + freeThrowFarEdgeFromBackboardM);
      const keyLineXFromBaselineM = Math.max(0, backboardM + freeThrowStrokeCenterFromBackboardM);
      return Object.freeze({
      keyFillXFromBaselineM: 0,
      keyFillLengthM,
      keyFillTopM: keyTopOuterM,
      keyFillBottomM: keyBottomOuterM,
      keyFillWidthM: keyWOuterM,
      keyLineXFromBaselineM,
      keyLineTopM: keyTopOuterM + (lineM * 0.5),
      keyLineBottomM: keyBottomOuterM - (lineM * 0.5),
      keyLineWidthM: Math.max(0, keyWOuterM - lineM),
      freeThrowCircleRStrokeCenterM: centerlineFromOuterRadius(official.ftR),
      threePointRStrokeCenterM: centerlineFromOuterRadius(official.threeR),
      threePointSidelineStrokeCenterOffsetM: centerlineFromInsideOffset(official.threeSideOffset),
      centerCircleRStrokeCenterM: centerR,
      centerInnerCircleRStrokeCenterM: centerInnerR,
      restrictedAreaRStrokeCenterM: restrictedR,
      restrictedAreaBasis: restrictedBasis || (Number.isFinite(restrictedOuter) ? 'outer-edge' : 'centerline'),
      restrictedAreaAppliesTo: official.restrictedAppliesTo || 'all',
      laneMarks,
      auxiliaryMarks: Object.freeze(auxiliaryMarks.slice()),
      officialBackboardFromEndlineM,
      backboardFromEndlineM: backboardM,
      backboardDeltaFromOfficialM: deltaM,
      rimCenterFromBackboardM,
      rimCenterFromEndlineM: backboardM + rimCenterFromBackboardM,
      freeThrowFarEdgeFromBackboardM,
      freeThrowFarEdgeFromBaselineM: keyFillLengthM,
      freeThrowStrokeCenterFromBackboardM,
      freeThrowStrokeCenterFromBaselineM: keyLineXFromBaselineM
      });
    };
    const sideBackboards = Object.freeze({
      left: readHoopAnchorOption('left', officialBackboardFromEndlineM),
      right: readHoopAnchorOption('right', officialBackboardFromEndlineM),
      top: readHoopAnchorOption('top', officialBackboardFromEndlineM),
      bottom: readHoopAnchorOption('bottom', officialBackboardFromEndlineM)
    });
    const sideDraws = Object.freeze({
      left: buildBasketballDraw(sideBackboards.left),
      right: buildBasketballDraw(sideBackboards.right),
      top: buildBasketballDraw(sideBackboards.top),
      bottom: buildBasketballDraw(sideBackboards.bottom)
    });
    const drawBasketball = sideDraws.left;
    const rimCenterFromEndlineM = drawBasketball.rimCenterFromEndlineM;
    objects.basketball = Object.freeze({
      equipment: Object.freeze(Object.assign({}, official.equipment || {})),
      left: Object.freeze({
        backboardFaceXFromLeftM: sideBackboards.left,
        backboardFromEndlineM: sideBackboards.left,
        rimCenterXFromLeftM: sideDraws.left.rimCenterFromEndlineM,
        rimCenterFromEndlineM: sideDraws.left.rimCenterFromEndlineM,
        rimCenterYM: officialWidthM * 0.5
      }),
      right: Object.freeze({
        backboardFaceXFromLeftM: officialLengthM - sideBackboards.right,
        backboardFromEndlineM: sideBackboards.right,
        rimCenterXFromLeftM: officialLengthM - sideDraws.right.rimCenterFromEndlineM,
        rimCenterFromEndlineM: sideDraws.right.rimCenterFromEndlineM,
        rimCenterYM: officialWidthM * 0.5
      }),
      top: Object.freeze({
        backboardFaceYFromTopM: sideBackboards.top,
        backboardFromEndlineM: sideBackboards.top,
        rimCenterYFromTopM: sideDraws.top.rimCenterFromEndlineM,
        rimCenterFromEndlineM: sideDraws.top.rimCenterFromEndlineM,
        rimCenterXM: officialLengthM * 0.5
      }),
      bottom: Object.freeze({
        backboardFaceYFromTopM: officialWidthM - sideBackboards.bottom,
        backboardFromEndlineM: sideBackboards.bottom,
        rimCenterYFromTopM: officialWidthM - sideDraws.bottom.rimCenterFromEndlineM,
        rimCenterFromEndlineM: sideDraws.bottom.rimCenterFromEndlineM,
        rimCenterXM: officialLengthM * 0.5
      })
    });
    ['left', 'right'].forEach((side) => {
      const sideDraw = sideDraws[side] || drawBasketball;
      const fromBaseline = (d) => mirrorXFromBaseline(side, d);
      const x0 = side === 'right' ? officialLengthM - sideDraw.keyFillLengthM : 0;
      const x1 = side === 'right' ? officialLengthM : sideDraw.keyFillLengthM;
      addRectPrimitive(side + 'KeyFill', Math.min(x0, x1), keyTopOuterM, sideDraw.keyFillLengthM, keyWOuterM, { sport: 'basketball', side, role: 'key-fill', basis: 'backboard-anchored' });
      addLinePrimitive(side + 'FreeThrowLine', fromBaseline(sideDraw.keyLineXFromBaselineM), Math.max(0, sideDraw.keyFillTopM - (lineM * 0.5)), fromBaseline(sideDraw.keyLineXFromBaselineM), Math.min(officialWidthM, sideDraw.keyFillBottomM + (lineM * 0.5)), { sport: 'basketball', side, role: 'free-throw-line', basis: 'stroke-center', spanBasis: 'outer-lane-stroke-edges' });
      addLinePrimitive(side + 'LaneLineTop', fromBaseline(0), sideDraw.keyLineTopM, fromBaseline(sideDraw.keyLineXFromBaselineM), sideDraw.keyLineTopM, { sport: 'basketball', side, role: 'lane-line', basis: 'stroke-center' });
      addLinePrimitive(side + 'LaneLineBottom', fromBaseline(0), sideDraw.keyLineBottomM, fromBaseline(sideDraw.keyLineXFromBaselineM), sideDraw.keyLineBottomM, { sport: 'basketball', side, role: 'lane-line', basis: 'stroke-center' });
      addArcPrimitive(side + 'FreeThrowCircle', fromBaseline(sideDraw.keyLineXFromBaselineM), officialWidthM * 0.5, sideDraw.freeThrowCircleRStrokeCenterM, { sport: 'basketball', side, role: 'free-throw-circle', basis: 'stroke-center' });
      addArcPrimitive(side + 'ThreePointLine', fromBaseline(sideDraw.rimCenterFromEndlineM), officialWidthM * 0.5, sideDraw.threePointRStrokeCenterM, { sport: 'basketball', side, role: 'three-point-line', basis: 'stroke-center', sidelineStrokeCenterOffsetM: sideDraw.threePointSidelineStrokeCenterOffsetM });
      if (restrictedR > 0) {
        addArcPrimitive(side + 'RestrictedAreaLine', fromBaseline(sideDraw.rimCenterFromEndlineM), officialWidthM * 0.5, restrictedR, { sport: 'basketball', side, role: 'restricted-area', basis: sideDraw.restrictedAreaBasis, appliesTo: sideDraw.restrictedAreaAppliesTo });
        if (rulesetId === 'FIBA' || rulesetId === 'NBA') {
          const cy = officialWidthM * 0.5;
          const xBackboard = fromBaseline(sideDraw.backboardFromEndlineM);
          const xRim = fromBaseline(sideDraw.rimCenterFromEndlineM);
          addLinePrimitive(side + 'RestrictedAreaConnectorTop', xBackboard, cy - restrictedR, xRim, cy - restrictedR, { sport: 'basketball', side, role: 'restricted-area-connector', basis: sideDraw.restrictedAreaBasis, appliesTo: sideDraw.restrictedAreaAppliesTo });
          addLinePrimitive(side + 'RestrictedAreaConnectorBottom', xBackboard, cy + restrictedR, xRim, cy + restrictedR, { sport: 'basketball', side, role: 'restricted-area-connector', basis: sideDraw.restrictedAreaBasis, appliesTo: sideDraw.restrictedAreaAppliesTo });
        }
      }
      sideDraw.laneMarks.forEach((mark, index) => {
        const x = fromBaseline(mark.offsetM);
        addLinePrimitive(side + 'LaneSpaceMarkTop' + index, x, sideDraw.keyLineTopM, x, sideDraw.keyLineTopM - mark.lenM, { sport: 'basketball', side, role: 'lane-space-mark', widthM: mark.widthM, basis: 'stroke-center' });
        addLinePrimitive(side + 'LaneSpaceMarkBottom' + index, x, sideDraw.keyLineBottomM, x, sideDraw.keyLineBottomM + mark.lenM, { sport: 'basketball', side, role: 'lane-space-mark', widthM: mark.widthM, basis: 'stroke-center' });
      });
      auxiliaryMarks.forEach((mark) => addPrimitive(Object.assign({ sport: 'basketball', role: 'official-auxiliary-mark' }, mapBasketballAuxMark(side, mark))));
    });
    addLinePrimitive('centerLine', officialLengthM * 0.5, -centerLineExtensionM, officialLengthM * 0.5, officialWidthM + centerLineExtensionM, { sport: 'basketball', role: 'center-line', basis: 'stroke-center' });
    addArcPrimitive('centerCircle', officialLengthM * 0.5, officialWidthM * 0.5, centerR, { sport: 'basketball', role: 'center-circle', basis: 'stroke-center' });
    if (centerInnerR > 0) addArcPrimitive('centerInnerCircle', officialLengthM * 0.5, officialWidthM * 0.5, centerInnerR, { sport: 'basketball', role: 'center-circle', basis: 'stroke-center' });
    features.basketball = Object.freeze({
      measurementBasis: official.measurementBasis || 'outside-edge',
      lineM,
      official: Object.freeze({
        lengthM: officialLengthM,
        widthM: officialWidthM,
        keyLengthFarEdgeM: keyLOuterM,
        keyWidthOuterEdgeM: keyWOuterM,
        freeThrowLineLengthM: officialPositiveM(official.freeThrowLineLength, keyWOuterM),
        freeThrowCircleOuterRadiusM: officialPositiveM(official.ftR, 0),
        threePointOuterRadiusM: officialPositiveM(official.threeR, 0),
        threePointSidelineOuterOffsetM: officialPositiveM(official.threeSideOffset, 0),
        centerLineExtensionM,
        backboardFromEndlineM: officialBackboardFromEndlineM,
        rimCenterFromBackboardM,
        equipment: Object.freeze(Object.assign({}, official.equipment || {}))
      }),
      sideBackboards,
      sideDraws,
      draw: drawBasketball
    });
  } else if (rulesetId === 'TENNIS') {
    const singlesWidth = officialPositiveM(official.singlesWidth, 8.23);
    const doublesMargin = Math.max(0, (officialWidthM - singlesWidth) * 0.5);
    const serviceFromNet = officialPositiveM(official.serviceLineFromNet, 6.4);
    const centerMarkLength = officialPositiveM(official.centerMarkLength, 0.1);
    const netX = officialLengthM * 0.5;
    const serviceLeft = netX - serviceFromNet;
    const serviceRight = netX + serviceFromNet;
    features.tennis = Object.freeze({
      measurementBasis: official.measurementBasis || 'outside-edge',
      singlesWidthM: singlesWidth,
      doublesAlleyWidthM: doublesMargin,
      serviceLineFromNetM: serviceFromNet,
      centerMarkLengthM: centerMarkLength
    });
    objects.tennis = Object.freeze({
      net: Object.freeze({
        xM: netX,
        y1M: 0,
        y2M: officialWidthM,
        anchorBasis: 'centerline',
        postCenterYTopM: -officialPositiveM(official.netPostOffsetFromDoublesSideline, 0.9144),
        postCenterYBottomM: officialWidthM + officialPositiveM(official.netPostOffsetFromDoublesSideline, 0.9144),
        postMaxDiameterM: officialPositiveM(official.netPostMaxDiameter, 0.15),
        heightAtPostsM: officialPositiveM(official.netHeightAtPosts, 1.07),
        heightAtCenterM: officialPositiveM(official.netHeightAtCenter, 0.914),
        singlesStickCenterYTopM: doublesMargin - officialPositiveM(official.singlesStickOffsetFromSinglesSideline, 0.9144),
        singlesStickCenterYBottomM: (officialWidthM - doublesMargin) + officialPositiveM(official.singlesStickOffsetFromSinglesSideline, 0.9144),
        singlesStickMaxDiameterM: officialPositiveM(official.singlesStickMaxDiameter, 0.075)
      })
    });
    addLinePrimitive('tennisDoublesSidelineTop', 0, 0, officialLengthM, 0, { sport: 'tennis', role: 'sideline', basis: 'inside-edge' });
    addLinePrimitive('tennisDoublesSidelineBottom', 0, officialWidthM, officialLengthM, officialWidthM, { sport: 'tennis', role: 'sideline', basis: 'inside-edge' });
    addLinePrimitive('tennisBaselineLeft', 0, 0, 0, officialWidthM, { sport: 'tennis', role: 'baseline', basis: 'inside-edge' });
    addLinePrimitive('tennisBaselineRight', officialLengthM, 0, officialLengthM, officialWidthM, { sport: 'tennis', role: 'baseline', basis: 'inside-edge' });
    addLinePrimitive('tennisSinglesSidelineTop', 0, doublesMargin, officialLengthM, doublesMargin, { sport: 'tennis', role: 'singles-sideline', basis: 'centerline' });
    addLinePrimitive('tennisSinglesSidelineBottom', 0, officialWidthM - doublesMargin, officialLengthM, officialWidthM - doublesMargin, { sport: 'tennis', role: 'singles-sideline', basis: 'centerline' });
    addLinePrimitive('tennisServiceLineLeft', serviceLeft, doublesMargin, serviceLeft, officialWidthM - doublesMargin, { sport: 'tennis', role: 'service-line', basis: 'centerline' });
    addLinePrimitive('tennisServiceLineRight', serviceRight, doublesMargin, serviceRight, officialWidthM - doublesMargin, { sport: 'tennis', role: 'service-line', basis: 'centerline' });
    addLinePrimitive('tennisCenterServiceLine', serviceLeft, officialWidthM * 0.5, serviceRight, officialWidthM * 0.5, { sport: 'tennis', role: 'center-service-line', basis: 'centerline' });
    addLinePrimitive('tennisNetLine', netX, 0, netX, officialWidthM, { sport: 'tennis', role: 'net-line', basis: 'centerline' });
    addLinePrimitive('tennisCenterMarkLeft', 0, officialWidthM * 0.5, centerMarkLength, officialWidthM * 0.5, { sport: 'tennis', role: 'center-mark', basis: 'inside-edge' });
    addLinePrimitive('tennisCenterMarkRight', officialLengthM, officialWidthM * 0.5, officialLengthM - centerMarkLength, officialWidthM * 0.5, { sport: 'tennis', role: 'center-mark', basis: 'inside-edge' });
  } else if (rulesetId === 'PICKLEBALL') {
    const nvz = officialPositiveM(official.nonVolleyZone, 2.1336);
    const centerX = officialLengthM * 0.5;
    const leftNvz = centerX - nvz + (lineM * 0.5);
    const rightNvz = centerX + nvz - (lineM * 0.5);
    features.pickleball = Object.freeze({
      measurementBasis: official.measurementBasis || 'outside-edge',
      nonVolleyZoneDepthM: nvz,
      nonVolleyZoneLineStrokeCentersM: Object.freeze({ left: leftNvz, right: rightNvz }),
      netLineXM: centerX
    });
    objects.pickleball = Object.freeze({
      net: Object.freeze({
        xM: centerX,
        y1M: 0,
        y2M: officialWidthM,
        anchorBasis: 'centerline',
        postInsideYTopM: (officialWidthM - officialPositiveM(official.netPostInsideDistance, 6.7056)) * 0.5,
        postInsideYBottomM: officialWidthM - ((officialWidthM - officialPositiveM(official.netPostInsideDistance, 6.7056)) * 0.5),
        postInsideDistanceM: officialPositiveM(official.netPostInsideDistance, 6.7056),
        postInsideDistanceToleranceM: officialPositiveM(official.netPostInsideDistanceTolerance, 0.0254),
        postMaxDiameterM: officialPositiveM(official.netPostMaxDiameter, 0.0762),
        netLengthMinM: officialPositiveM(official.netLengthMin, 6.6294),
        heightAtSidelinesM: officialPositiveM(official.netHeightAtSidelines, 0.9144),
        heightAtCenterM: officialPositiveM(official.netHeightAtCenter, 0.8636),
        heightToleranceM: officialPositiveM(official.netHeightTolerance, 0.00635)
      })
    });
    addLinePrimitive('pickleSidelineTop', 0, 0, officialLengthM, 0, { sport: 'pickleball', role: 'sideline', basis: 'inside-edge' });
    addLinePrimitive('pickleSidelineBottom', 0, officialWidthM, officialLengthM, officialWidthM, { sport: 'pickleball', role: 'sideline', basis: 'inside-edge' });
    addLinePrimitive('pickleBaselineLeft', 0, 0, 0, officialWidthM, { sport: 'pickleball', role: 'baseline', basis: 'inside-edge' });
    addLinePrimitive('pickleBaselineRight', officialLengthM, 0, officialLengthM, officialWidthM, { sport: 'pickleball', role: 'baseline', basis: 'inside-edge' });
    addLinePrimitive('pickleNvzLineLeft', leftNvz, 0, leftNvz, officialWidthM, { sport: 'pickleball', role: 'non-volley-zone-line', basis: 'centerline' });
    addLinePrimitive('pickleNvzLineRight', rightNvz, 0, rightNvz, officialWidthM, { sport: 'pickleball', role: 'non-volley-zone-line', basis: 'centerline' });
    addLinePrimitive('pickleCenterLineLeft', 0, officialWidthM * 0.5, leftNvz, officialWidthM * 0.5, { sport: 'pickleball', role: 'centerline', basis: 'centerline' });
    addLinePrimitive('pickleCenterLineRight', rightNvz, officialWidthM * 0.5, officialLengthM, officialWidthM * 0.5, { sport: 'pickleball', role: 'centerline', basis: 'centerline' });
    addLinePrimitive('pickleNetLine', centerX, 0, centerX, officialWidthM, { sport: 'pickleball', role: 'net-line', basis: 'centerline', objectOnly: true });
  } else if (rulesetId === 'PADEL') {
    const serviceFromBack = officialPositiveM(official.serviceLineFromBackWall, 3.05);
    const centerExtension = officialPositiveM(official.centerServiceExtension, 0.2);
    const leftService = serviceFromBack;
    const rightService = officialLengthM - serviceFromBack;
    features.padel = Object.freeze({
      measurementBasis: official.measurementBasis || 'centerline',
      serviceLineFromNetM: officialPositiveM(official.serviceLineFromNet, 6.95),
      serviceLineFromBackWallM: serviceFromBack,
      centerServiceExtensionM: centerExtension
    });
    objects.padel = Object.freeze({
      net: Object.freeze({
        xM: officialLengthM * 0.5,
        y1M: 0,
        y2M: officialWidthM,
        anchorBasis: 'centerline',
        heightAtCenterM: officialPositiveM(official.netHeightAtCenter, 0.88),
        heightAtEndsM: officialPositiveM(official.netHeightAtEnds, 0.92),
        heightToleranceM: officialPositiveM(official.netHeightTolerance, 0.005),
        postMaxHeightM: officialPositiveM(official.netPostMaxHeight, 1.05),
        cableMaxDiameterM: officialPositiveM(official.netCableMaxDiameter, 0.01)
      }),
      enclosure: Object.freeze({ xM: 0, yM: 0, wM: officialLengthM, hM: officialWidthM, anchorBasis: 'interior-measurement' })
    });
    addLinePrimitive('padelSidelineTop', 0, 0, officialLengthM, 0, { sport: 'padel', role: 'sideline', basis: 'inside-edge' });
    addLinePrimitive('padelSidelineBottom', 0, officialWidthM, officialLengthM, officialWidthM, { sport: 'padel', role: 'sideline', basis: 'inside-edge' });
    addLinePrimitive('padelBaselineLeft', 0, 0, 0, officialWidthM, { sport: 'padel', role: 'baseline', basis: 'inside-edge' });
    addLinePrimitive('padelBaselineRight', officialLengthM, 0, officialLengthM, officialWidthM, { sport: 'padel', role: 'baseline', basis: 'inside-edge' });
    addLinePrimitive('padelServiceLineLeft', leftService, 0, leftService, officialWidthM, { sport: 'padel', role: 'service-line', basis: 'centerline' });
    addLinePrimitive('padelServiceLineRight', rightService, 0, rightService, officialWidthM, { sport: 'padel', role: 'service-line', basis: 'centerline' });
    addLinePrimitive('padelCenterLine', Math.max(0, leftService - centerExtension), officialWidthM * 0.5, Math.min(officialLengthM, rightService + centerExtension), officialWidthM * 0.5, { sport: 'padel', role: 'center-service-line', basis: 'centerline' });
    addLinePrimitive('padelNetLine', officialLengthM * 0.5, 0, officialLengthM * 0.5, officialWidthM, { sport: 'padel', role: 'net-line', basis: 'centerline' });
  } else if (rulesetId === 'VOLLEYBALL') {
    const half = officialLengthM * 0.5;
    const attack = officialPositiveM(official.attackLineFromCenter, 3);
    const attackCenters = [half - attack + (lineM * 0.5), half + attack - (lineM * 0.5)];
    const serviceMarkLength = officialPositiveM(official.serviceZoneMarkLength, 0.15);
    const serviceMarkGap = officialPositiveM(official.serviceZoneMarkGapFromEndline, 0.2);
    const attackDash = officialPositiveM(official.attackExtensionDashLength, 0.15);
    const attackGap = officialPositiveM(official.attackExtensionGap, 0.2);
    const attackCount = Math.max(0, Math.round(Number(official.attackExtensionCount) || 5));
    features.volleyball = Object.freeze({
      measurementBasis: 'rear-edge',
      attackLineRearEdgesFromLeftM: Object.freeze([half - attack, half + attack]),
      attackLineStrokeCentersFromLeftM: Object.freeze(attackCenters.slice()),
      attackExtensionDashLengthM: attackDash,
      attackExtensionGapM: attackGap,
      attackExtensionCount: attackCount,
      serviceZoneMarkLengthM: serviceMarkLength,
      serviceZoneMarkGapFromEndlineM: serviceMarkGap
    });
    objects.volleyball = Object.freeze({
      net: Object.freeze({
        xM: half,
        y1M: 0,
        y2M: officialWidthM,
        anchorBasis: 'centerline-axis',
        heightMenM: officialPositiveM(official.netHeightMen, 2.43),
        heightWomenM: officialPositiveM(official.netHeightWomen, 2.24)
      })
    });
    addBoundaryPrimitives('volleyball', 'volleyball', officialLengthM, officialWidthM, { endline: 'endline' });
    addLinePrimitive('volleyballCenterLine', half, 0, half, officialWidthM, { sport: 'volleyball', role: 'center-line', basis: 'centerline-axis' });
    addLinePrimitive('volleyballLeftAttackLine', attackCenters[0], 0, attackCenters[0], officialWidthM, { sport: 'volleyball', role: 'attack-line', basis: 'rear-edge' });
    addLinePrimitive('volleyballRightAttackLine', attackCenters[1], 0, attackCenters[1], officialWidthM, { sport: 'volleyball', role: 'attack-line', basis: 'rear-edge' });
    attackCenters.forEach((x, lineIndex) => {
      for (let i = 0; i < attackCount; i += 1) {
        const topY2 = -(attackGap + (i * (attackDash + attackGap)));
        const topY1 = topY2 - attackDash;
        const bottomY1 = officialWidthM + attackGap + (i * (attackDash + attackGap));
        const bottomY2 = bottomY1 + attackDash;
        addLinePrimitive('volleyballAttackExtension' + lineIndex + 'Top' + i, x, topY1, x, topY2, { sport: 'volleyball', role: 'attack-line-extension', basis: 'rear-edge' });
        addLinePrimitive('volleyballAttackExtension' + lineIndex + 'Bottom' + i, x, bottomY1, x, bottomY2, { sport: 'volleyball', role: 'attack-line-extension', basis: 'rear-edge' });
      }
    });
    [[0, 'Left'], [officialLengthM, 'Right']].forEach(([baselineX, sideLabel]) => {
      const dir = baselineX === 0 ? -1 : 1;
      const x1 = baselineX + (dir * serviceMarkGap);
      const x2 = baselineX + (dir * (serviceMarkGap + serviceMarkLength));
      addLinePrimitive('volleyball' + sideLabel + 'ServiceZoneTop', x1, 0, x2, 0, { sport: 'volleyball', role: 'service-zone-mark', basis: 'outside-endline' });
      addLinePrimitive('volleyball' + sideLabel + 'ServiceZoneBottom', x1, officialWidthM, x2, officialWidthM, { sport: 'volleyball', role: 'service-zone-mark', basis: 'outside-endline' });
    });
  } else if (rulesetId === 'FUTSAL') {
    const futsalFeature = {
      measurementBasis: official.measurementBasis || 'outside-goalpost',
      penaltyAreaDepthM: officialPositiveM(official.penaltyDepth, 6),
      penaltyAreaWidthM: officialPositiveM(official.penaltySpan, 15.16),
      penaltyAreaArcRadiusM: officialPositiveM(official.penaltyDepth, 6),
      penaltyAreaJoinLineLengthM: officialPositiveM(official.goalOutsideWidth, 3.16),
      penaltyAreaArcCenterOffsetFromGoalCenterM: officialPositiveM(official.goalOutsideWidth, 3.16) * 0.5,
      penaltyMarkCenterFromGoalLineM: officialPositiveM(official.penaltyMark, 6),
      secondPenaltyMarkCenterFromGoalLineM: officialPositiveM(official.secondPenaltyMark, 10),
      penaltyMarkRadiusM: officialPositiveM(official.penaltyMarkRadius, 0.06),
      goalkeeperMarkFromSecondM: officialPositiveM(official.goalkeeperMarkFromSecond, 5),
      goalkeeperMarkLengthM: officialPositiveM(official.goalkeeperMarkLength, 0.4),
      goalkeeperMarkBackEdgeFromGoalLineM: Math.max(0, officialPositiveM(official.secondPenaltyMark, 10) - officialPositiveM(official.goalkeeperMarkFromSecond, 5)),
      goalkeeperMarkStrokeCenterFromGoalLineM: Math.max(0, officialPositiveM(official.secondPenaltyMark, 10) - officialPositiveM(official.goalkeeperMarkFromSecond, 5) + (lineM * 0.5)),
      secondPenaltySideMarkOffsetM: officialPositiveM(official.secondPenaltySideMarkOffset, 5),
      secondPenaltySideMarkRadiusM: officialPositiveM(official.secondPenaltySideMarkRadius, 0.04),
      substitutionMarkLengthM: officialPositiveM(official.substitutionMarkLength, 0.8),
      substitutionMarkDistanceFromHalfwayM: officialPositiveM(official.substitutionMarkDistanceFromHalfway, 5),
      substitutionZoneLengthM: officialPositiveM(official.substitutionZoneLength, 5),
      substitutionTouchline: String(official.substitutionTouchline || 'top').trim().toLowerCase(),
      cornerArcRadiusM: officialPositiveM(official.cornerR, 0.25),
      cornerDistanceMarkDistanceM: officialPositiveM(official.cornerDistanceMarkDistance, 5),
      cornerDistanceMarkGapFromGoalLineM: officialPositiveM(official.cornerDistanceMarkGapFromGoalLine, 0.05),
      cornerDistanceMarkLengthM: officialPositiveM(official.cornerDistanceMarkLength, 0.4)
    };
    features.futsal = Object.freeze({
      measurementBasis: futsalFeature.measurementBasis,
      penaltyAreaDepthM: futsalFeature.penaltyAreaDepthM,
      penaltyAreaWidthM: futsalFeature.penaltyAreaWidthM,
      penaltyAreaArcRadiusM: futsalFeature.penaltyAreaArcRadiusM,
      penaltyAreaJoinLineLengthM: futsalFeature.penaltyAreaJoinLineLengthM,
      penaltyAreaArcCenterOffsetFromGoalCenterM: futsalFeature.penaltyAreaArcCenterOffsetFromGoalCenterM,
      penaltyMarkCenterFromGoalLineM: futsalFeature.penaltyMarkCenterFromGoalLineM,
      secondPenaltyMarkCenterFromGoalLineM: futsalFeature.secondPenaltyMarkCenterFromGoalLineM,
      penaltyMarkRadiusM: futsalFeature.penaltyMarkRadiusM,
      goalkeeperMarkFromSecondM: futsalFeature.goalkeeperMarkFromSecondM,
      goalkeeperMarkLengthM: futsalFeature.goalkeeperMarkLengthM,
      goalkeeperMarkBackEdgeFromGoalLineM: futsalFeature.goalkeeperMarkBackEdgeFromGoalLineM,
      goalkeeperMarkStrokeCenterFromGoalLineM: futsalFeature.goalkeeperMarkStrokeCenterFromGoalLineM,
      secondPenaltySideMarkOffsetM: futsalFeature.secondPenaltySideMarkOffsetM,
      secondPenaltySideMarkRadiusM: futsalFeature.secondPenaltySideMarkRadiusM,
      substitutionMarkLengthM: futsalFeature.substitutionMarkLengthM,
      substitutionMarkDistanceFromHalfwayM: futsalFeature.substitutionMarkDistanceFromHalfwayM,
      substitutionZoneLengthM: futsalFeature.substitutionZoneLengthM,
      substitutionTouchline: futsalFeature.substitutionTouchline,
      cornerArcRadiusM: futsalFeature.cornerArcRadiusM,
      cornerDistanceMarkDistanceM: futsalFeature.cornerDistanceMarkDistanceM,
      cornerDistanceMarkGapFromGoalLineM: futsalFeature.cornerDistanceMarkGapFromGoalLineM,
      cornerDistanceMarkLengthM: futsalFeature.cornerDistanceMarkLengthM,
      goalPostWidthM: officialPositiveM(official.goalPostWidth, 0.08),
      goalOutsideWidthM: officialPositiveM(official.goalOutsideWidth, 3.16)
    });
    objects.futsal = Object.freeze({
      leftGoal: Object.freeze({ xM: 0, yCenterM: officialWidthM * 0.5, widthM: officialPositiveM(official.goalWidth, 3), outsideWidthM: officialPositiveM(official.goalOutsideWidth, 3.16), heightM: officialPositiveM(official.goalHeight, 2), postWidthM: officialPositiveM(official.goalPostWidth, 0.08) }),
      rightGoal: Object.freeze({ xM: officialLengthM, yCenterM: officialWidthM * 0.5, widthM: officialPositiveM(official.goalWidth, 3), outsideWidthM: officialPositiveM(official.goalOutsideWidth, 3.16), heightM: officialPositiveM(official.goalHeight, 2), postWidthM: officialPositiveM(official.goalPostWidth, 0.08) })
    });
    addBoundaryPrimitives('futsal', 'futsal', officialLengthM, officialWidthM, { endline: 'goal-line', sideline: 'touchline' });
    addLinePrimitive('futsalHalfwayLine', officialLengthM * 0.5, 0, officialLengthM * 0.5, officialWidthM, { sport: 'futsal', role: 'halfway-line', basis: 'centerline' });
    addCirclePrimitive('futsalCenterCircle', officialLengthM * 0.5, officialWidthM * 0.5, officialPositiveM(official.centerR, 3), { sport: 'futsal', role: 'center-circle', basis: 'centerline' });
    addPointPrimitive('futsalCenterMark', officialLengthM * 0.5, officialWidthM * 0.5, futsalFeature.penaltyMarkRadiusM, { sport: 'futsal', role: 'center-mark', basis: 'centerline' });
    const futsalCenterY = officialWidthM * 0.5;
    const penaltyDepth = futsalFeature.penaltyAreaDepthM;
    const penaltyJoinHalf = futsalFeature.penaltyAreaJoinLineLengthM * 0.5;
    const penaltyArcRadius = futsalFeature.penaltyAreaArcRadiusM;
    ['left', 'right'].forEach((side) => {
      const mirror = side === 'right';
      const xFromGoal = (d) => mirror ? officialLengthM - d : d;
      const linePrefix = side === 'right' ? 'Right' : 'Left';
      addArcPrimitive('futsal' + linePrefix + 'PenaltyAreaUpperArc', xFromGoal(0), futsalCenterY - penaltyJoinHalf, penaltyArcRadius, {
        sport: 'futsal',
        side,
        role: 'penalty-area-arc',
        basis: 'outside-goalpost',
        joinLineLengthM: futsalFeature.penaltyAreaJoinLineLengthM,
        arcCenterOffsetFromGoalCenterM: futsalFeature.penaltyAreaArcCenterOffsetFromGoalCenterM,
        startAngleRad: mirror ? Math.PI : -Math.PI / 2,
        endAngleRad: mirror ? Math.PI * 1.5 : 0
      });
      addLinePrimitive('futsal' + linePrefix + 'PenaltyAreaJoin', xFromGoal(penaltyDepth), futsalCenterY - penaltyJoinHalf, xFromGoal(penaltyDepth), futsalCenterY + penaltyJoinHalf, { sport: 'futsal', side, role: 'penalty-area-join', basis: 'centerline' });
      addArcPrimitive('futsal' + linePrefix + 'PenaltyAreaLowerArc', xFromGoal(0), futsalCenterY + penaltyJoinHalf, penaltyArcRadius, {
        sport: 'futsal',
        side,
        role: 'penalty-area-arc',
        basis: 'outside-goalpost',
        joinLineLengthM: futsalFeature.penaltyAreaJoinLineLengthM,
        arcCenterOffsetFromGoalCenterM: futsalFeature.penaltyAreaArcCenterOffsetFromGoalCenterM,
        startAngleRad: mirror ? Math.PI / 2 : 0,
        endAngleRad: mirror ? Math.PI : Math.PI / 2
      });
      addPointPrimitive('futsal' + linePrefix + 'PenaltyMark', xFromGoal(futsalFeature.penaltyMarkCenterFromGoalLineM), futsalCenterY, futsalFeature.penaltyMarkRadiusM, { sport: 'futsal', side, role: 'penalty-mark', basis: 'centerline' });
      addPointPrimitive('futsal' + linePrefix + 'SecondPenaltyMark', xFromGoal(futsalFeature.secondPenaltyMarkCenterFromGoalLineM), futsalCenterY, futsalFeature.penaltyMarkRadiusM, { sport: 'futsal', side, role: 'second-penalty-mark', basis: 'centerline' });
      addPointPrimitive('futsal' + linePrefix + 'SecondPenaltySideMarkTop', xFromGoal(futsalFeature.secondPenaltyMarkCenterFromGoalLineM), futsalCenterY - futsalFeature.secondPenaltySideMarkOffsetM, futsalFeature.secondPenaltySideMarkRadiusM, { sport: 'futsal', side, role: 'second-penalty-side-mark', basis: 'centerline' });
      addPointPrimitive('futsal' + linePrefix + 'SecondPenaltySideMarkBottom', xFromGoal(futsalFeature.secondPenaltyMarkCenterFromGoalLineM), futsalCenterY + futsalFeature.secondPenaltySideMarkOffsetM, futsalFeature.secondPenaltySideMarkRadiusM, { sport: 'futsal', side, role: 'second-penalty-side-mark', basis: 'centerline' });
      addLinePrimitive('futsal' + linePrefix + 'GoalkeeperDistanceMark', xFromGoal(futsalFeature.goalkeeperMarkStrokeCenterFromGoalLineM), futsalCenterY - (futsalFeature.goalkeeperMarkLengthM * 0.5), xFromGoal(futsalFeature.goalkeeperMarkStrokeCenterFromGoalLineM), futsalCenterY + (futsalFeature.goalkeeperMarkLengthM * 0.5), { sport: 'futsal', side, role: 'goalkeeper-distance-mark', basis: 'back-edge' });
    });
    const subY = futsalFeature.substitutionTouchline === 'bottom' ? officialWidthM : 0;
    const subHalf = officialLengthM * 0.5;
    const subHalfMark = futsalFeature.substitutionMarkLengthM * 0.5;
    const subY1 = futsalFeature.substitutionTouchline === 'bottom' ? officialWidthM - subHalfMark : -subHalfMark;
    const subY2 = futsalFeature.substitutionTouchline === 'bottom' ? officialWidthM + subHalfMark : subHalfMark;
    [-1, 1].forEach((dir, index) => {
      const nearHalf = subHalf + (dir * futsalFeature.substitutionMarkDistanceFromHalfwayM);
      const farHalf = subHalf + (dir * (futsalFeature.substitutionMarkDistanceFromHalfwayM + futsalFeature.substitutionZoneLengthM));
      addLinePrimitive('futsalSubstitutionZoneNear' + index, nearHalf, subY1, nearHalf, subY2, { sport: 'futsal', role: 'substitution-zone-mark', basis: 'touchline', touchlineM: subY });
      addLinePrimitive('futsalSubstitutionZoneFar' + index, farHalf, subY1, farHalf, subY2, { sport: 'futsal', role: 'substitution-zone-mark', basis: 'touchline', touchlineM: subY });
    });
    [[0, 0, 0, Math.PI / 2, 'TopLeft'], [officialLengthM, 0, Math.PI / 2, Math.PI, 'TopRight'], [officialLengthM, officialWidthM, Math.PI, Math.PI * 1.5, 'BottomRight'], [0, officialWidthM, Math.PI * 1.5, Math.PI * 2, 'BottomLeft']].forEach((corner) => {
      addArcPrimitive('futsalCornerArc' + corner[4], corner[0], corner[1], futsalFeature.cornerArcRadiusM, { sport: 'futsal', role: 'corner-arc', basis: 'centerline', startAngleRad: corner[2], endAngleRad: corner[3] });
    });
    const cdmGap = futsalFeature.cornerDistanceMarkGapFromGoalLineM;
    const cdmLen = futsalFeature.cornerDistanceMarkLengthM;
    const cdmDist = futsalFeature.cornerDistanceMarkDistanceM;
    [cdmDist, officialWidthM - cdmDist].forEach((y, index) => {
      addLinePrimitive('futsalLeftCornerDistanceMark' + index, -cdmGap, y, -(cdmGap + cdmLen), y, { sport: 'futsal', role: 'corner-distance-mark', basis: 'outside-goal-line' });
      addLinePrimitive('futsalRightCornerDistanceMark' + index, officialLengthM + cdmGap, y, officialLengthM + cdmGap + cdmLen, y, { sport: 'futsal', role: 'corner-distance-mark', basis: 'outside-goal-line' });
    });
  } else if (rulesetId === 'NETBALL') {
    const poleMin = officialPositiveM(official.goalPostPoleDiameterMin, 0.065);
    const poleMax = officialPositiveM(official.goalPostPoleDiameterMax, 0.1);
    const paddingMax = officialPositiveM(official.goalPostPaddingMax, 0.05);
    const visiblePostRadius = (poleMax * 0.5) + paddingMax;
    features.netball = Object.freeze({
      measurementBasis: official.measurementBasis || 'line-included',
      goalCircleCenterM: Object.freeze({ leftX: 0, rightX: officialLengthM, y: officialWidthM * 0.5 }),
      goalCircleRadiusM: officialPositiveM(official.goalCircleR, 4.9),
      centerCircleRadiusM: officialPositiveM(official.centerCircleR, 0.45),
      goalPostAnchorBasis: 'outside-goal-line-midpoint',
      goalPostPoleDiameterMinM: poleMin,
      goalPostPoleDiameterMaxM: poleMax,
      goalPostVisibleRadiusM: visiblePostRadius,
      goalPostHeightM: officialPositiveM(official.goalPostHeight, 3.05),
      goalRingInternalDiameterM: officialPositiveM(official.goalRingInternalDiameter, 0.38),
      goalRingRodDiameterM: officialPositiveM(official.goalRingRodDiameter, 0.015),
      goalRingBarLengthM: officialPositiveM(official.goalRingBarLength, 0.15)
    });
    objects.netball = Object.freeze({
      leftPost: Object.freeze({ xM: visiblePostRadius, backEdgeXM: 0, yM: officialWidthM * 0.5, anchorBasis: 'back-of-pole-at-outer-edge-of-goal-line', poleDiameterMinM: poleMin, poleDiameterMaxM: poleMax, visibleRadiusM: visiblePostRadius }),
      rightPost: Object.freeze({ xM: officialLengthM - visiblePostRadius, backEdgeXM: officialLengthM, yM: officialWidthM * 0.5, anchorBasis: 'back-of-pole-at-outer-edge-of-goal-line', poleDiameterMinM: poleMin, poleDiameterMaxM: poleMax, visibleRadiusM: visiblePostRadius })
    });
    addBoundaryPrimitives('netball', 'netball', officialLengthM, officialWidthM, { endline: 'goal-line', sideline: 'side-line' });
    addLinePrimitive('netballTransverseLineLeft', officialLengthM / 3, 0, officialLengthM / 3, officialWidthM, { sport: 'netball', role: 'transverse-line', basis: 'line-included' });
    addLinePrimitive('netballTransverseLineRight', (officialLengthM * 2) / 3, 0, (officialLengthM * 2) / 3, officialWidthM, { sport: 'netball', role: 'transverse-line', basis: 'line-included' });
    addArcPrimitive('netballLeftGoalCircle', 0, officialWidthM * 0.5, officialPositiveM(official.goalCircleR, 4.9), { sport: 'netball', role: 'goal-circle', basis: 'line-included' });
    addArcPrimitive('netballRightGoalCircle', officialLengthM, officialWidthM * 0.5, officialPositiveM(official.goalCircleR, 4.9), { sport: 'netball', role: 'goal-circle', basis: 'line-included' });
    addCirclePrimitive('netballCenterCircle', officialLengthM * 0.5, officialWidthM * 0.5, officialPositiveM(official.centerCircleR, 0.45), { sport: 'netball', role: 'center-circle', basis: 'line-included' });
    addPointPrimitive('netballLeftGoalPostMarker', visiblePostRadius, officialWidthM * 0.5, visiblePostRadius, { sport: 'netball', role: 'goal-post-marker', basis: 'back-of-pole-at-outer-edge-of-goal-line' });
    addPointPrimitive('netballRightGoalPostMarker', officialLengthM - visiblePostRadius, officialWidthM * 0.5, visiblePostRadius, { sport: 'netball', role: 'goal-post-marker', basis: 'back-of-pole-at-outer-edge-of-goal-line' });
  } else if (rulesetId === 'SHUFFLEBOARD') {
    const shuffleFeature = {
      measurementBasis: 'centerline',
      lineM,
      baseLineFromEndM: officialPositiveM(official.baseLine, 1.9812),
      tenOffDepthM: officialPositiveM(official.tenOffDepth, 0.4572),
      scoringBandM: officialPositiveM(official.scoringBand, 0.9144),
      triangleDepthM: officialPositiveM(official.triangleDepth, 2.7432),
      deadLineGapM: officialPositiveM(official.deadLineGap, 0.9144),
      tenOffSideInsetM: officialPositiveM(official.tenOffSideInset, 0.1524),
      tenOffSeparatorHalfBaseM: officialPositiveM(official.tenOffSeparatorHalfBase, 0.0381)
    };
    features.shuffleboard = Object.freeze({
      measurementBasis: shuffleFeature.measurementBasis,
      lineM: shuffleFeature.lineM,
      baseLineFromEndM: shuffleFeature.baseLineFromEndM,
      tenOffDepthM: shuffleFeature.tenOffDepthM,
      scoringBandM: shuffleFeature.scoringBandM,
      triangleDepthM: shuffleFeature.triangleDepthM,
      deadLineGapM: shuffleFeature.deadLineGapM,
      tenOffSideInsetM: shuffleFeature.tenOffSideInsetM,
      tenOffSeparatorHalfBaseM: shuffleFeature.tenOffSeparatorHalfBaseM
    });
    addBoundaryPrimitives('shuffle', 'shuffleboard', officialLengthM, officialWidthM, { sideline: 'sideline', endline: 'endline' });
    addLinePrimitive('shuffleCenterLine', 0, officialWidthM * 0.5, officialLengthM, officialWidthM * 0.5, { sport: 'shuffleboard', role: 'center-line', basis: 'centerline' });
    ['left', 'right'].forEach((side) => {
      const fromEnd = (d) => side === 'right' ? officialLengthM - d : d;
      const label = side === 'right' ? 'Right' : 'Left';
      const base = shuffleFeature.baseLineFromEndM + shuffleFeature.tenOffDepthM;
      const sevenEight = base + shuffleFeature.scoringBandM;
      const eightTen = base + (shuffleFeature.scoringBandM * 2);
      const tip = base + shuffleFeature.triangleDepthM;
      const cy = officialWidthM * 0.5;
      const mirror = side === 'right';
      const lineTo = (id, d1, y1, d2, y2, role) => addLinePrimitive('shuffle' + label + id, fromEnd(d1), y1, fromEnd(d2), y2, { sport: 'shuffleboard', side, role, basis: 'centerline' });
      addLinePrimitive('shuffle' + side + 'BaseLine', fromEnd(shuffleFeature.baseLineFromEndM), 0, fromEnd(shuffleFeature.baseLineFromEndM), officialWidthM, { sport: 'shuffleboard', role: 'baseline', basis: 'centerline' });
      addLinePrimitive('shuffle' + side + 'TenOffLine', fromEnd(shuffleFeature.baseLineFromEndM + shuffleFeature.tenOffDepthM), 0, fromEnd(shuffleFeature.baseLineFromEndM + shuffleFeature.tenOffDepthM), officialWidthM, { sport: 'shuffleboard', role: 'ten-off-line', basis: 'centerline' });
      addLinePrimitive('shuffle' + side + 'DeadLine', fromEnd(shuffleFeature.baseLineFromEndM + shuffleFeature.tenOffDepthM + shuffleFeature.triangleDepthM + shuffleFeature.deadLineGapM), 0, fromEnd(shuffleFeature.baseLineFromEndM + shuffleFeature.tenOffDepthM + shuffleFeature.triangleDepthM + shuffleFeature.deadLineGapM), officialWidthM, { sport: 'shuffleboard', role: 'dead-line', basis: 'centerline' });
      lineTo('TriangleSideTop', base, 0, tip, cy, 'scoring-triangle-side');
      lineTo('TriangleSideBottom', base, officialWidthM, tip, cy, 'scoring-triangle-side');
      lineTo('SevenEightSeparator', sevenEight, officialWidthM * 0.25, sevenEight, officialWidthM * 0.75, 'scoring-zone-separator');
      lineTo('EightTenSeparator', eightTen, officialWidthM * 0.375, eightTen, officialWidthM * 0.625, 'scoring-zone-separator');
      lineTo('TriangleCenterSeparator', base, cy, tip, cy, 'scoring-centerline');
      lineTo('TenOffSideTop', shuffleFeature.baseLineFromEndM, shuffleFeature.tenOffSideInsetM, base, 0, 'ten-off-side');
      lineTo('TenOffSideBottom', shuffleFeature.baseLineFromEndM, officialWidthM - shuffleFeature.tenOffSideInsetM, base, officialWidthM, 'ten-off-side');
      if (mirror) {
        // Kept for metadata parity; x coordinates are already mirrored by fromEnd().
      }
    });
  } else if (rulesetId === 'CORNHOLE') {
    const boardLengthM = officialPositiveM(official.boardLength, 1.2192);
    const boardWidthM = officialPositiveM(official.boardWidth, 0.6096);
    const boardFrontGapM = officialPositiveM(official.boardFrontGap, 8.2296);
    const pitcherBoxLengthM = officialPositiveM(official.pitcherBoxLength, 1.2192);
    const pitcherBoxWidthM = officialPositiveM(official.pitcherBoxWidth, 0.9144);
    const endClearanceM = officialPositiveM(official.endClearance, Math.max(0, (officialLengthM - boardFrontGapM - (boardLengthM * 2)) * 0.5));
    const sideClearanceM = officialPositiveM(official.sideClearance, Math.max(0, (officialWidthM - boardWidthM - (pitcherBoxWidthM * 2)) * 0.5));
    const holeDiameterM = officialPositiveM(official.holeDiameter, 0.1524);
    const holeCenterFromBackM = officialPositiveM(official.holeCenterFromBack, 0.2286);
    const holeCenterFromSideM = officialPositiveM(official.holeCenterFromSide, boardWidthM * 0.5);
    const boardY = sideClearanceM + pitcherBoxWidthM;
    const leftBoardX = endClearanceM;
    const rightBoardX = officialLengthM - endClearanceM - boardLengthM;
    const leftFrontX = leftBoardX + boardLengthM;
    const rightFrontX = rightBoardX;
    const boxTopY = sideClearanceM;
    const boxBottomY = boardY + boardWidthM;
    const centerY = boardY + holeCenterFromSideM;
    features.cornhole = Object.freeze({
      measurementBasis: official.measurementBasis || 'court-layout',
      lineM,
      courtLengthM: officialLengthM,
      courtWidthM: officialWidthM,
      boardLengthM,
      boardWidthM,
      boardFrontGapM,
      pitcherBoxLengthM,
      pitcherBoxWidthM,
      endClearanceM,
      sideClearanceM,
      holeDiameterM,
      holeCenterFromBackM,
      holeCenterFromSideM,
      boardFrontLinesM: Object.freeze({ leftX: leftFrontX, rightX: rightFrontX }),
      boardRectsM: Object.freeze({
        left: Object.freeze({ xM: leftBoardX, yM: boardY, wM: boardLengthM, hM: boardWidthM }),
        right: Object.freeze({ xM: rightBoardX, yM: boardY, wM: boardLengthM, hM: boardWidthM })
      }),
      pitcherBoxesM: Object.freeze({
        leftUpper: Object.freeze({ xM: leftBoardX, yM: boxTopY, wM: pitcherBoxLengthM, hM: pitcherBoxWidthM }),
        leftLower: Object.freeze({ xM: leftBoardX, yM: boxBottomY, wM: pitcherBoxLengthM, hM: pitcherBoxWidthM }),
        rightUpper: Object.freeze({ xM: rightBoardX, yM: boxTopY, wM: pitcherBoxLengthM, hM: pitcherBoxWidthM }),
        rightLower: Object.freeze({ xM: rightBoardX, yM: boxBottomY, wM: pitcherBoxLengthM, hM: pitcherBoxWidthM })
      }),
      holesM: Object.freeze({
        left: Object.freeze({ xM: leftBoardX + holeCenterFromBackM, yM: centerY, rM: holeDiameterM * 0.5 }),
        right: Object.freeze({ xM: rightBoardX + boardLengthM - holeCenterFromBackM, yM: centerY, rM: holeDiameterM * 0.5 })
      })
    });
    addBoundaryPrimitives('cornhole', 'cornhole', officialLengthM, officialWidthM, { sideline: 'court-boundary', endline: 'court-boundary' });
    addRectPrimitive('cornholeLeftPitcherBoxUpper', leftBoardX, boxTopY, pitcherBoxLengthM, pitcherBoxWidthM, { sport: 'cornhole', side: 'left', role: 'pitcher-box', basis: 'outer-edge' });
    addRectPrimitive('cornholeLeftPitcherBoxLower', leftBoardX, boxBottomY, pitcherBoxLengthM, pitcherBoxWidthM, { sport: 'cornhole', side: 'left', role: 'pitcher-box', basis: 'outer-edge' });
    addRectPrimitive('cornholeLeftBoardOutline', leftBoardX, boardY, boardLengthM, boardWidthM, { sport: 'cornhole', side: 'left', role: 'board-outline', basis: 'outer-edge' });
    addLinePrimitive('cornholeLeftFoulLine', leftFrontX, boxTopY, leftFrontX, boxBottomY + pitcherBoxWidthM, { sport: 'cornhole', side: 'left', role: 'foul-line', basis: 'front-edge-of-board' });
    addCirclePrimitive('cornholeLeftBoardHole', leftBoardX + holeCenterFromBackM, centerY, holeDiameterM * 0.5, { sport: 'cornhole', side: 'left', role: 'board-hole', basis: 'centerline' });
    addRectPrimitive('cornholeRightPitcherBoxUpper', rightBoardX, boxTopY, pitcherBoxLengthM, pitcherBoxWidthM, { sport: 'cornhole', side: 'right', role: 'pitcher-box', basis: 'outer-edge' });
    addRectPrimitive('cornholeRightPitcherBoxLower', rightBoardX, boxBottomY, pitcherBoxLengthM, pitcherBoxWidthM, { sport: 'cornhole', side: 'right', role: 'pitcher-box', basis: 'outer-edge' });
    addRectPrimitive('cornholeRightBoardOutline', rightBoardX, boardY, boardLengthM, boardWidthM, { sport: 'cornhole', side: 'right', role: 'board-outline', basis: 'outer-edge' });
    addLinePrimitive('cornholeRightFoulLine', rightFrontX, boxTopY, rightFrontX, boxBottomY + pitcherBoxWidthM, { sport: 'cornhole', side: 'right', role: 'foul-line', basis: 'front-edge-of-board' });
    addCirclePrimitive('cornholeRightBoardHole', rightBoardX + boardLengthM - holeCenterFromBackM, centerY, holeDiameterM * 0.5, { sport: 'cornhole', side: 'right', role: 'board-hole', basis: 'centerline' });
  }

  function renderedPrimitive(primitive) {
    const p = Object.assign({}, primitive || {});
    const sx = (x) => playableRectM.x + ((Number(x) || 0) * scale);
    const sy = (y) => playableRectM.y + ((Number(y) || 0) * scale);
    ['x1M', 'x2M', 'xM', 'cxM'].forEach((key) => {
      if (Number.isFinite(Number(p[key]))) p[key] = sx(Number(p[key]));
    });
    ['y1M', 'y2M', 'yM', 'cyM'].forEach((key) => {
      if (Number.isFinite(Number(p[key]))) p[key] = sy(Number(p[key]));
    });
    ['wM', 'hM', 'rM', 'radiusYM', 'lineWidthM', 'widthM', 'lengthM'].forEach((key) => {
      if (Number.isFinite(Number(p[key]))) p[key] = Math.max(0, Number(p[key]) * scale);
    });
    p.sourceScale = scale;
    p.transformMode = transformMode;
    return Object.freeze(p);
  }
  function primitiveBounds(primitive) {
    const p = primitive || {};
    const xs = [];
    const ys = [];
    const push = (x, y) => {
      if (Number.isFinite(Number(x))) xs.push(Number(x));
      if (Number.isFinite(Number(y))) ys.push(Number(y));
    };
    if (p.kind === 'rect') {
      push(p.xM, p.yM);
      push(Number(p.xM) + Number(p.wM), Number(p.yM) + Number(p.hM));
    } else if (p.kind === 'circle' || p.kind === 'point') {
      const r = Number(p.rM) || 0;
      push(Number(p.xM) - r, Number(p.yM) - r);
      push(Number(p.xM) + r, Number(p.yM) + r);
    } else if (p.kind === 'arc') {
      const r = Number(p.rM) || 0;
      push(Number(p.cxM) - r, Number(p.cyM) - r);
      push(Number(p.cxM) + r, Number(p.cyM) + r);
    } else {
      push(p.x1M, p.y1M);
      push(p.x2M, p.y2M);
      push(p.xM, p.yM);
      push(p.cxM, p.cyM);
    }
    if (!xs.length || !ys.length) return null;
    return {
      minX: Math.min.apply(Math, xs),
      minY: Math.min.apply(Math, ys),
      maxX: Math.max.apply(Math, xs),
      maxY: Math.max.apply(Math, ys)
    };
  }
  const officialExtents = drawPrimitives.reduce((acc, primitive) => {
    const b = primitiveBounds(primitive);
    if (!b) return acc;
    acc.minX = Math.min(acc.minX, b.minX);
    acc.minY = Math.min(acc.minY, b.minY);
    acc.maxX = Math.max(acc.maxX, b.maxX);
    acc.maxY = Math.max(acc.maxY, b.maxY);
    return acc;
  }, { minX: 0, minY: 0, maxX: officialLengthM, maxY: officialWidthM });
  const outsideFeatureExtentsM = Object.freeze({
    leftM: Math.max(0, -officialExtents.minX),
    rightM: Math.max(0, officialExtents.maxX - officialLengthM),
    topM: Math.max(0, -officialExtents.minY),
    bottomM: Math.max(0, officialExtents.maxY - officialWidthM)
  });
  const renderedPrimitives = Object.freeze(drawPrimitives.map(renderedPrimitive));

  return Object.freeze({
    rulesetId,
    noSeries: !!options.noSeries,
    source: Object.freeze({
      title: official.source || String(rulesetId),
      url: OFFICIAL_SOURCE_URLS[rulesetId] || ''
    }),
    official: Object.freeze(official),
    officialLengthM,
    officialWidthM,
    officialPlayableSize: Object.freeze({ lengthM: officialLengthM, widthM: officialWidthM }),
    lineWidthM: lineM,
    measurementBasis: official.measurementBasis || null,
    transform: Object.freeze({
      mode: transformMode,
      scale,
      rectM: Object.freeze(Object.assign({}, playableRectM)),
      visibleRectM: Object.freeze(Object.assign({}, visibleRectM))
    }),
    transformMode,
    scale,
    playableRectM: Object.freeze(playableRectM),
    visibleRectM: Object.freeze(visibleRectM),
    clipRectM: Object.freeze(Object.assign({}, visibleRectM)),
    objectAnchors: Object.freeze({
      officialBackboardFromEndlineM: officialPositiveM(official.hoopToEnd, 0),
      officialRimCenterFromEndlineM: officialPositiveM(official.hoopToEnd, 0) + officialPositiveM(official.rimFromBackboard, 0),
      backboardFromEndlineM: objects.basketball && objects.basketball.left
        ? objects.basketball.left.backboardFromEndlineM
        : officialPositiveM(official.hoopToEnd, 0),
      rimCenterFromEndlineM: objects.basketball && objects.basketball.left
        ? objects.basketball.left.rimCenterFromEndlineM
        : officialPositiveM(official.hoopToEnd, 0) + officialPositiveM(official.rimFromBackboard, 0),
      left: objects.basketball && objects.basketball.left ? objects.basketball.left : null,
      right: objects.basketball && objects.basketball.right ? objects.basketball.right : null,
      top: objects.basketball && objects.basketball.top ? objects.basketball.top : null,
      bottom: objects.basketball && objects.basketball.bottom ? objects.basketball.bottom : null
    }),
    objects: Object.freeze(objects),
    features: Object.freeze(features),
    drawPrimitives: Object.freeze(drawPrimitives.slice()),
    renderedPrimitives,
    outsideFeatureExtentsM
  });
}

const RULESETS = {
  FIBA: {
    id: 'FIBA',
    playableLength: 28,
    playableWidth: 15,
    singleHoopLength: 14,
    linesGlobal: 'SwishFibaLines',
    detailKey: 'FIBA',
    useHoops: true,
    geometry: {
      line: 0.05,
      keyW: 4.9,
      keyL: 5.8,
      centerR: 1.8,
      centerLineExtension: 0.15,
      ftR: 1.8,
      // FIBA marks the no-charge semi-circle at a 1.30 m outer edge with a 0.05 m line.
      // Canvas strokes are centerline-based, so the radius used for drawing is 1.275 m.
      restrictedR: 1.275,
      threeR: 6.75,
      // Official FIBA: 1.20 m from endline to the backboard face.
      hoopToEnd: 1.2,
      // FIBA spec commonly uses 1.575 m from endline to rim center → 0.375 m from backboard to rim center.
      rimFromBackboard: 0.375
    }
  },
  NBA: {
    id: 'NBA',
    playableLength: 28.6512,
    playableWidth: 15.24,
    // Official one-hoop playable surface is one regulation half court.
    singleHoopLength: 14.3256,
    linesGlobal: 'SwishNbaLines',
    detailKey: 'FIBA',
    useHoops: true,
    geometry: {
      line: 0.0508,
      keyW: 4.8768,
      keyL: 5.7912,
      centerR: 1.8288,
      ftR: 1.8288,
      restrictedR: 1.2192,
      threeR: 7.239,
      // Official NBA overhang: 4 ft (1.2192 m) from endline to backboard.
      // Ensures 2D hoop distance adjustments keep FT/3PT spacing anchored to the rim/backboard.
      hoopToEnd: 1.2192,
      // 15 in from backboard to rim center.
      rimFromBackboard: 0.381
    }
  },
  NCAA: {
    id: 'NCAA',
    playableLength: 28.6512,
    playableWidth: 15.24,
    // Official one-hoop playable surface is one regulation half court.
    singleHoopLength: 14.3256,
    linesGlobal: 'SwishNcaaLines',
    detailKey: 'FIBA',
    useHoops: true,
    geometry: {
      line: 0.0508,
      keyW: 3.6576,
      // Baseline → free-throw line: 19 ft (15 ft from backboard + 4 ft overhang).
      keyL: 5.7912,
      centerR: 1.8288,
      ftR: 1.8288,
      restrictedR: 1.2192,
      threeR: 6.75,
      // Official NCAA overhang: 4 ft (1.2192 m) from endline to backboard.
      hoopToEnd: 1.2192,
      // 15 in from backboard to rim center.
      rimFromBackboard: 0.381
    }
  },
  US_HIGHSCHOOL: {
    id: 'US_HIGHSCHOOL',
    playableLength: 25.6032,
    playableWidth: 15.24,
    // Official one-hoop playable surface is one regulation half court.
    singleHoopLength: 12.8016,
    linesGlobal: 'SwishUsaHighSchoolLines',
    detailKey: 'FIBA',
    useHoops: true,
    geometry: {
      line: 0.0508,
      keyW: 3.6576,
      // Baseline → free-throw line: 19 ft (15 ft from backboard + 4 ft overhang).
      keyL: 5.7912,
      centerR: 1.8288,
      ftR: 1.8288,
      restrictedR: 0,
      threeR: 6.0198,
      // Official NFHS/US High School overhang: 4 ft (1.2192 m) from endline to backboard.
      hoopToEnd: 1.2192,
      // 15 in from backboard to rim center.
      rimFromBackboard: 0.381
    }
  },
  TENNIS: {
    id: 'TENNIS',
    playableLength: 23.77,
    playableWidth: 10.97,
    singleHoopLength: 11.885,
    linesGlobal: 'SwishTennisLines',
    detailKey: 'TENNIS',
    useHoops: true,
    geometry: {
      line: 0.0508,
      keyW: 4.115,
      keyL: 6.4,
      centerR: 0,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 1.575
    }
  },
  PICKLEBALL: {
    id: 'PICKLEBALL',
    playableLength: 13.4112,
    playableWidth: 6.096,
    singleHoopLength: 6.7056,
    linesGlobal: 'SwishPickleballLines',
    detailKey: 'PICKLEBALL',
    useHoops: true,
    geometry: {
      line: 0.0508,
      keyW: 0,
      keyL: 0,
      centerR: 0,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 0
    }
  },
  PADEL: {
    id: 'PADEL',
    playableLength: 20,
    playableWidth: 10,
    singleHoopLength: 10,
    linesGlobal: 'SwishPadelLines',
    detailKey: 'PADEL',
    useHoops: true,
    geometry: {
      line: 0.05,
      keyW: 0,
      keyL: 0,
      centerR: 0,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 0
    }
  },
  VOLLEYBALL: {
    id: 'VOLLEYBALL',
    playableLength: 18,
    playableWidth: 9,
    singleHoopLength: 9,
    linesGlobal: 'SwishVolleyballLines',
    detailKey: 'VOLLEYBALL',
    useHoops: true,
    geometry: {
      line: 0.05,
      keyW: 0,
      keyL: 0,
      centerR: 0,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 0
    }
  },
  SHUFFLEBOARD: {
    id: 'SHUFFLEBOARD',
    playableLength: 15.8496,
    playableWidth: 1.8288,
    singleHoopLength: 15.8496,
    linesGlobal: 'SwishShuffleboardLines',
    detailKey: 'SHUFFLEBOARD',
    useHoops: true,
    geometry: {
      line: 0.0254,
      keyW: 0,
      keyL: 0,
      centerR: 0,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 0
    }
  },
  CORNHOLE: {
    id: 'CORNHOLE',
    playableLength: 13.716,
    playableWidth: 3.048,
    singleHoopLength: 13.716,
    linesGlobal: 'SwishCornholeLines',
    detailKey: 'CORNHOLE',
    useHoops: true,
    geometry: {
      line: 0.05,
      keyW: 0,
      keyL: 0,
      centerR: 0,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 0
    }
  },
  FUTSAL: {
    id: 'FUTSAL',
    playableLength: 40,
    playableWidth: 20,
    singleHoopLength: 40,
    linesGlobal: 'SwishFutsalLines',
    detailKey: 'FUTSAL',
    useHoops: true,
    geometry: {
      line: 0.08,
      keyW: 15.16,
      keyL: 6,
      centerR: 3,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 0
    }
  },
  NETBALL: {
    id: 'NETBALL',
    playableLength: 30.5,
    playableWidth: 15.25,
    singleHoopLength: 30.5,
    linesGlobal: 'SwishNetballLines',
    detailKey: 'NETBALL',
    useHoops: true,
    geometry: {
      line: 0.05,
      keyW: 4.9,
      keyL: 4.9,
      centerR: 0.45,
      ftR: 0,
      threeR: 0,
      hoopToEnd: 0
    }
  }
};
Object.keys(OFFICIAL_GEOMETRY).forEach((rulesetId) => {
  if (!RULESETS[rulesetId]) return;
  RULESETS[rulesetId].geometry = cloneOfficialGeometry(rulesetId);
});
const EXTRA_LINE_TYPES = [
  {
    id: 'fiba3',
    label: 'FIBA 3-point',
    ruleset: 'FIBA',
    parts: ['leftThreePointLine', 'rightThreePointLine', 'topThreePointLine', 'bottomThreePointLine'],
    defaultColor: '#FFFFFF'
  },
  {
    id: 'nba3',
    label: 'NBA 3-point',
    ruleset: 'NBA',
    parts: ['leftThreePointLine', 'rightThreePointLine', 'topThreePointLine', 'bottomThreePointLine'],
    defaultColor: '#FFFFFF'
  },
  {
    id: 'ncaa3',
    label: 'NCAA 3-point',
    ruleset: 'NCAA',
    parts: ['leftThreePointLine', 'rightThreePointLine', 'topThreePointLine', 'bottomThreePointLine'],
    defaultColor: '#FFFFFF'
  },
  {
    id: 'usahs3',
    label: 'USA High School 3-point',
    ruleset: 'US_HIGHSCHOOL',
    parts: ['leftThreePointLine', 'rightThreePointLine', 'topThreePointLine', 'bottomThreePointLine'],
    defaultColor: '#FFFFFF'
  },
  {
    id: 'tennis',
    label: 'Tennis lines',
    ruleset: 'TENNIS',
    parts: [
      'tennisDoublesSidelineTop',
      'tennisDoublesSidelineBottom',
      'tennisSinglesSidelineTop',
      'tennisSinglesSidelineBottom',
      'tennisBaselineLeft',
      'tennisBaselineRight',
      'tennisServiceLineLeft',
      'tennisServiceLineRight',
      'tennisCenterServiceLine',
      'tennisNetLine',
      'tennisCenterMarks'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  },
  {
    id: 'pickleball',
    label: 'Pickleball lines',
    ruleset: 'PICKLEBALL',
    parts: [
      'pickleSidelineTop',
      'pickleSidelineBottom',
      'pickleBaselineLeft',
      'pickleBaselineRight',
      'pickleCenterLine',
      'pickleNvzLineLeft',
      'pickleNvzLineRight'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  },
  {
    id: 'padel',
    label: 'Padel lines',
    ruleset: 'PADEL',
    parts: [
      'padelSidelineTop',
      'padelSidelineBottom',
      'padelBaselineLeft',
      'padelBaselineRight',
      'padelServiceLineLeft',
      'padelServiceLineRight',
      'padelCenterLine',
      'padelNetLine'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  },
  {
    id: 'volleyball',
    label: 'Volleyball lines',
    ruleset: 'VOLLEYBALL',
    parts: [
      'volleyballSidelineTop',
      'volleyballSidelineBottom',
      'volleyballBaselineLeft',
      'volleyballBaselineRight',
      'volleyballCenterLine',
      'volleyballLeftAttackLine',
      'volleyballRightAttackLine',
      'volleyballLeftAttackExtensionTop',
      'volleyballLeftAttackExtensionBottom',
      'volleyballRightAttackExtensionTop',
      'volleyballRightAttackExtensionBottom',
      'volleyballLeftServiceZoneTop',
      'volleyballLeftServiceZoneBottom',
      'volleyballRightServiceZoneTop',
      'volleyballRightServiceZoneBottom'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  },
  {
    id: 'shuffleboard',
    label: 'Shuffleboard lines',
    ruleset: 'SHUFFLEBOARD',
    parts: [
      'shuffleSidelineTop',
      'shuffleSidelineBottom',
      'shuffleEndLineLeft',
      'shuffleEndLineRight',
      'shuffleLeftBaseLine',
      'shuffleLeftTenOffLine',
      'shuffleLeftTriangleSideTop',
      'shuffleLeftTriangleSideBottom',
      'shuffleLeftSevenEightLine',
      'shuffleLeftEightTenLine',
      'shuffleLeftCenterLine',
      'shuffleLeftTenOffTriangle',
      'shuffleLeftDeadLine',
      'shuffleLeftLabels',
      'shuffleRightBaseLine',
      'shuffleRightTenOffLine',
      'shuffleRightTriangleSideTop',
      'shuffleRightTriangleSideBottom',
      'shuffleRightSevenEightLine',
      'shuffleRightEightTenLine',
      'shuffleRightCenterLine',
      'shuffleRightTenOffTriangle',
      'shuffleRightDeadLine',
      'shuffleRightLabels'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  },
  {
    id: 'cornhole',
    label: 'Cornhole lines',
    ruleset: 'CORNHOLE',
    parts: [
      'cornholeSidelineTop',
      'cornholeSidelineBottom',
      'cornholeEndLineLeft',
      'cornholeEndLineRight',
      'cornholeLeftPitcherBoxUpper',
      'cornholeLeftPitcherBoxLower',
      'cornholeLeftBoardOutline',
      'cornholeLeftFoulLine',
      'cornholeLeftBoardHole',
      'cornholeRightPitcherBoxUpper',
      'cornholeRightPitcherBoxLower',
      'cornholeRightBoardOutline',
      'cornholeRightFoulLine',
      'cornholeRightBoardHole'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  },
  {
    id: 'futsal',
    label: 'Futsal lines',
    ruleset: 'FUTSAL',
    parts: [
      'futsalSidelineTop',
      'futsalSidelineBottom',
      'futsalGoalLineLeft',
      'futsalGoalLineRight',
      'futsalHalfwayLine',
      'futsalCenterCircle',
      'futsalLeftPenaltyAreaLine',
      'futsalRightPenaltyAreaLine',
      'futsalLeftPenaltyMark',
      'futsalRightPenaltyMark',
      'futsalLeftSecondPenaltyMark',
      'futsalRightSecondPenaltyMark',
      'futsalLeftSecondPenaltySideMarks',
      'futsalRightSecondPenaltySideMarks',
      'futsalLeftGoalkeeperDistanceMark',
      'futsalRightGoalkeeperDistanceMark',
      'futsalCornerArcs',
      'futsalCornerDistanceMarks',
      'futsalSubstitutionMarks',
      'futsalGoalLeft',
      'futsalGoalRight'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  },
  {
    id: 'netball',
    label: 'Netball lines',
    ruleset: 'NETBALL',
    parts: [
      'netballSidelineTop',
      'netballSidelineBottom',
      'netballGoalLineLeft',
      'netballGoalLineRight',
      'netballTransverseLineLeft',
      'netballTransverseLineRight',
      'netballCenterCircle',
      'netballLeftGoalCircleLine',
      'netballRightGoalCircleLine',
      'netballGoalPostLeft',
      'netballGoalPostRight'
    ],
    defaultColor: '#FFFFFF',
    paintMode: 'footprint-fixed'
  }
];
const REPEATABLE_EXTRA_SPORT_IDS = Object.freeze([
  'tennis',
  'pickleball',
  'padel',
  'volleyball',
  'shuffleboard',
  'cornhole',
  'futsal',
  'netball'
]);
const BASE_EXTRA_LINE_TYPES = EXTRA_LINE_TYPES.slice();

function normalizeExtraLineTypeId(value) {
  return String(value || '').trim().toLowerCase();
}

function getExtraLineTypeDefinition(extraId) {
  const wanted = normalizeExtraLineTypeId(extraId);
  if (!wanted) return null;
  return EXTRA_LINE_TYPES.find(def => def && normalizeExtraLineTypeId(def.id) === wanted) || null;
}

function parseExtraLineTypeInstanceId(extraId) {
  const wanted = normalizeExtraLineTypeId(extraId);
  const match = wanted.match(/^([a-z][a-z0-9]*)-(\d+)$/);
  if (!match) return null;
  const baseId = match[1];
  const instanceNumber = Number(match[2]);
  if (!REPEATABLE_EXTRA_SPORT_IDS.includes(baseId) || !Number.isInteger(instanceNumber) || instanceNumber < 2) return null;
  return { id: wanted, baseId, instanceNumber };
}

function ensureExtraLineTypeInstanceDefinition(extraId, baseIdHint = '') {
  const wanted = normalizeExtraLineTypeId(extraId);
  if (!wanted) return null;
  const existing = getExtraLineTypeDefinition(wanted);
  if (existing) return existing;
  const parsed = parseExtraLineTypeInstanceId(wanted);
  const hintedBaseId = normalizeExtraLineTypeId(baseIdHint);
  const baseId = parsed ? parsed.baseId : hintedBaseId;
  const instanceNumber = parsed ? parsed.instanceNumber : null;
  if (!baseId || !instanceNumber || !REPEATABLE_EXTRA_SPORT_IDS.includes(baseId)) return null;
  const base = BASE_EXTRA_LINE_TYPES.find(def => def && normalizeExtraLineTypeId(def.id) === baseId);
  if (!base) return null;
  const sportLabel = String(base.label || base.id || 'Court').replace(/\s+lines?$/i, '');
  const def = Object.assign({}, base, {
    id: wanted,
    label: `${sportLabel} ${instanceNumber} lines`,
    instanceOf: baseId,
    instanceNumber
  });
  if (Array.isArray(base.parts)) def.parts = base.parts.slice();
  EXTRA_LINE_TYPES.push(def);
  return def;
}

function removeExtraLineTypeInstanceDefinition(extraId) {
  const wanted = normalizeExtraLineTypeId(extraId);
  if (!wanted) return false;
  const index = EXTRA_LINE_TYPES.findIndex(def => (
    def &&
    def.instanceOf &&
    normalizeExtraLineTypeId(def.id) === wanted
  ));
  if (index < 0) return false;
  EXTRA_LINE_TYPES.splice(index, 1);
  return true;
}

function getExtraLineTypeBaseId(extraId) {
  const def = getExtraLineTypeDefinition(extraId);
  if (def) return normalizeExtraLineTypeId(def.instanceOf || def.id);
  const parsed = parseExtraLineTypeInstanceId(extraId);
  return parsed ? parsed.baseId : normalizeExtraLineTypeId(extraId);
}
const EXTRA_SCALE_MIN = 0;
const EXTRA_SCALE_MAX = 2;
const EXTRA_ROT_MAX = 360;
const THREE_MEASURE_DEDUP_EPS_M = 1e-3;
const THREE_MEASURE_ROT_DEG_BY_RULESET = Object.freeze({
  FIBA: 0,
  NCAA: 0,
  US_HIGHSCHOOL: 15,
  NBA: 30
});
const THREE_MEASURE_ALIGN_RULESETS = new Set(['FIBA', 'NCAA', 'US_HIGHSCHOOL', 'NBA']);
const DEFAULT_RULESET = 'FIBA';
const DEFAULT_COURT_TYPE = RULESET_TO_COURT_TYPE[DEFAULT_RULESET] || 'fiba';
const FUTSAL_STANDARD_PLAYING_LENGTH_M = 40;
const FUTSAL_STANDARD_PLAYING_WIDTH_M = 20;
const FUTSAL_STANDARD_MIN_LENGTH_M = 25;
const FUTSAL_STANDARD_MIN_WIDTH_M = 16;
const FUTSAL_DEFAULT_PENALTY_WIDTH_M = 15.16;
const FUTSAL_DEFAULT_PENALTY_LENGTH_M = 6;
const NETBALL_OFFICIAL_PLAYING_LENGTH_M = 30.5;
const NETBALL_OFFICIAL_PLAYING_WIDTH_M = 15.25;
const NETBALL_DEFAULT_GOAL_CIRCLE_RADIUS_M = 4.9;
const NETBALL_CENTER_CIRCLE_RADIUS_M = 0.45;
const DETAIL_LIBRARY = {
  FIBA: {
    groups: [
      { id: 'borders', label: 'Borders' },
      { id: 'perimeter', label: 'Perimeter' },
      { id: 'center', label: 'Center' },
      { id: 'left', label: 'Left hoop' },
      { id: 'right', label: 'Right hoop' },
      { id: 'top', label: 'Top hoop' },
      { id: 'bottom', label: 'Bottom hoop' }
    ],
    parts: [
      { id: 'centerLine', label: 'Center line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'centerCircleLine', label: 'Center circle', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'boundaryLine', label: 'Boundary line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'outerPerimeterLine', label: 'Outer perimeter line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'leftThreePointArea', label: '3-point area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'leftMidRangeArea', label: 'Mid-range area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'leftKey', label: 'Key', group: 'left', side: 'left', role: 'key', fallback: 'key' },
      { id: 'leftThreePointLine', label: '3-point line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'leftFreeThrowLines', label: 'Free throw line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'leftFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'leftFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'leftRestrictedAreaLine', label: 'Restricted area', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'leftBorderLine', label: 'Sideline/baseline', group: 'left', side: 'left', role: 'line', fallback: 'lines', compatOnly: true, hiddenInUi: true },
      { id: 'leftInboundMarks', label: 'Inbound/throw-in marks', group: 'left', side: 'left', role: 'line', fallback: 'lines', compatOnly: true, hiddenInUi: true },
      { id: 'leftOfficialAuxiliaryMarks', label: 'Official auxiliary marks', group: 'left', side: 'left', role: 'line', fallback: 'lines', compatOnly: true, hiddenInUi: true },
      { id: 'borderSideTopFill', label: 'Top sideline border', group: 'borders', side: 'center', role: 'area', fallback: 'border' },
      { id: 'borderSideBottomFill', label: 'Bottom sideline border', group: 'borders', side: 'center', role: 'area', fallback: 'border' },
      { id: 'borderEndLeftFill', label: 'Left baseline border', group: 'borders', side: 'left', role: 'area', fallback: 'border' },
      { id: 'rightThreePointArea', label: '3-point area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'rightMidRangeArea', label: 'Mid-range area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'rightKey', label: 'Key', group: 'right', side: 'right', role: 'key', fallback: 'key' },
      { id: 'rightThreePointLine', label: '3-point line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'rightFreeThrowLines', label: 'Free throw line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'rightFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'rightFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'rightRestrictedAreaLine', label: 'Restricted area', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'rightBorderLine', label: 'Sideline/baseline', group: 'right', side: 'right', role: 'line', fallback: 'lines', compatOnly: true, hiddenInUi: true },
      { id: 'rightInboundMarks', label: 'Inbound/throw-in marks', group: 'right', side: 'right', role: 'line', fallback: 'lines', compatOnly: true, hiddenInUi: true },
      { id: 'rightOfficialAuxiliaryMarks', label: 'Official auxiliary marks', group: 'right', side: 'right', role: 'line', fallback: 'lines', compatOnly: true, hiddenInUi: true },
      // 3-hoops mode: additional hoop centered on the top edge (sideline). These parts are drawn via an overlay pass.
      { id: 'topKey', label: 'Key', group: 'top', side: 'top', role: 'key', fallback: 'key' },
      { id: 'topThreePointLine', label: '3-point line', group: 'top', side: 'top', role: 'line', fallback: 'lines' },
      { id: 'topFreeThrowLines', label: 'Free throw line', group: 'top', side: 'top', role: 'line', fallback: 'lines' },
      { id: 'topFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'top', side: 'top', role: 'line', fallback: 'lines' },
      { id: 'topFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'top', side: 'top', role: 'line', fallback: 'lines' },
      { id: 'topRestrictedAreaLine', label: 'Restricted area', group: 'top', side: 'top', role: 'line', fallback: 'lines' },
      // 4-hoops mode: additional hoop centered on the bottom edge (sideline). These parts are drawn via an overlay pass.
      { id: 'bottomKey', label: 'Key', group: 'bottom', side: 'bottom', role: 'key', fallback: 'key' },
      { id: 'bottomThreePointLine', label: '3-point line', group: 'bottom', side: 'bottom', role: 'line', fallback: 'lines' },
      { id: 'bottomFreeThrowLines', label: 'Free throw line', group: 'bottom', side: 'bottom', role: 'line', fallback: 'lines' },
      { id: 'bottomFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'bottom', side: 'bottom', role: 'line', fallback: 'lines' },
      { id: 'bottomFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'bottom', side: 'bottom', role: 'line', fallback: 'lines' },
      { id: 'bottomRestrictedAreaLine', label: 'Restricted area', group: 'bottom', side: 'bottom', role: 'line', fallback: 'lines' },
      { id: 'borderEndRightFill', label: 'Right baseline border', group: 'borders', side: 'right', role: 'area', fallback: 'border' }
    ]
  },
  TENNIS: {
    groups: [
      { id: 'center', label: 'Court lines' },
      { id: 'left', label: 'Left side' },
      { id: 'right', label: 'Right side' },
      { id: 'borders', label: 'Borders' }
    ],
    parts: [
      { id: 'tennisLeftNoMansLand', label: 'Backcourt', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'tennisLeftLeftServiceCourt', label: 'Top service court', group: 'left', side: 'left', role: 'area', fallback: 'key' },
      { id: 'tennisLeftRightServiceCourt', label: 'Bottom service court', group: 'left', side: 'left', role: 'area', fallback: 'key' },
      { id: 'tennisLeftLeftDoublesAlley', label: 'Top doubles alley', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'tennisLeftRightDoublesAlley', label: 'Bottom doubles alley', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'tennisRightNoMansLand', label: 'Backcourt', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'tennisRightLeftServiceCourt', label: 'Top service court', group: 'right', side: 'right', role: 'area', fallback: 'key' },
      { id: 'tennisRightRightServiceCourt', label: 'Bottom service court', group: 'right', side: 'right', role: 'area', fallback: 'key' },
      { id: 'tennisRightLeftDoublesAlley', label: 'Top doubles alley', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'tennisRightRightDoublesAlley', label: 'Bottom doubles alley', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'tennisDoublesSidelineTop', label: 'Top doubles sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisDoublesSidelineBottom', label: 'Bottom doubles sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisSinglesSidelineTop', label: 'Top singles sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisSinglesSidelineBottom', label: 'Bottom singles sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisBaselineLeft', label: 'Left baseline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisBaselineRight', label: 'Right baseline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisServiceLineLeft', label: 'Left service line', group: 'center', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'tennisServiceLineRight', label: 'Right service line', group: 'center', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'tennisCenterServiceLine', label: 'Center service line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisNetLine', label: 'Net line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'tennisCenterMarks', label: 'Center marks', group: 'center', side: 'center', role: 'line', fallback: 'lines' }
    ]
  },
  PICKLEBALL: {
    groups: [
      { id: 'center', label: 'Court lines' },
      { id: 'left', label: 'Left side' },
      { id: 'right', label: 'Right side' },
      { id: 'borders', label: 'Borders' }
    ],
    parts: [
      { id: 'pickleLeftUpperService', label: 'Top service court', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'pickleLeftLowerService', label: 'Bottom service court', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'pickleLeftKitchen', label: 'Kitchen', group: 'left', side: 'left', role: 'area', fallback: 'key' },
      { id: 'pickleRightUpperService', label: 'Top service court', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'pickleRightLowerService', label: 'Bottom service court', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'pickleRightKitchen', label: 'Kitchen', group: 'right', side: 'right', role: 'area', fallback: 'key' },
      { id: 'pickleSidelineTop', label: 'Top sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'pickleSidelineBottom', label: 'Bottom sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'pickleBaselineLeft', label: 'Left baseline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'pickleBaselineRight', label: 'Right baseline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'pickleCenterLine', label: 'Center line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'pickleNvzLineLeft', label: 'Left non-volley line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'pickleNvzLineRight', label: 'Right non-volley line', group: 'center', side: 'center', role: 'line', fallback: 'lines' }
    ]
  },
  PADEL: {
    groups: [
      { id: 'perimeter', label: 'Perimeter' },
      { id: 'center', label: 'Center' },
      { id: 'left', label: 'Left side' },
      { id: 'right', label: 'Right side' }
    ],
    parts: [
      { id: 'padelLeftBackcourt', label: 'Backcourt', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'padelLeftServiceTop', label: 'Top service box', group: 'left', side: 'left', role: 'area', fallback: 'key' },
      { id: 'padelLeftServiceBottom', label: 'Bottom service box', group: 'left', side: 'left', role: 'area', fallback: 'key' },
      { id: 'padelRightServiceTop', label: 'Top service box', group: 'right', side: 'right', role: 'area', fallback: 'key' },
      { id: 'padelRightServiceBottom', label: 'Bottom service box', group: 'right', side: 'right', role: 'area', fallback: 'key' },
      { id: 'padelRightBackcourt', label: 'Backcourt', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'padelSidelineTop', label: 'Top sideline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'padelSidelineBottom', label: 'Bottom sideline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'padelBaselineLeft', label: 'Left baseline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'padelBaselineRight', label: 'Right baseline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'padelServiceLineLeft', label: 'Service line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'padelServiceLineRight', label: 'Service line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'padelCenterLine', label: 'Center service line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'padelNetLine', label: 'Net line', group: 'center', side: 'center', role: 'line', fallback: 'lines' }
    ]
  },
  VOLLEYBALL: {
    groups: [
      { id: 'center', label: 'Court lines' },
      { id: 'left', label: 'Left side' },
      { id: 'right', label: 'Right side' },
      { id: 'borders', label: 'Borders' }
    ],
    parts: [
      { id: 'volleyballLeftBackZone', label: 'Back zone', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'volleyballLeftFrontZone', label: 'Front zone', group: 'left', side: 'left', role: 'area', fallback: 'key' },
      { id: 'volleyballRightFrontZone', label: 'Front zone', group: 'right', side: 'right', role: 'area', fallback: 'key' },
      { id: 'volleyballRightBackZone', label: 'Back zone', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'volleyballSidelineTop', label: 'Top sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballSidelineBottom', label: 'Bottom sideline', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballBaselineLeft', label: 'Left end line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballBaselineRight', label: 'Right end line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballCenterLine', label: 'Center line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballLeftAttackLine', label: 'Left attack line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballRightAttackLine', label: 'Right attack line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballLeftAttackExtensionTop', label: 'Left attack extension top', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballLeftAttackExtensionBottom', label: 'Left attack extension bottom', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballRightAttackExtensionTop', label: 'Right attack extension top', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballRightAttackExtensionBottom', label: 'Right attack extension bottom', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballLeftServiceZoneTop', label: 'Left top service mark', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballLeftServiceZoneBottom', label: 'Left bottom service mark', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballRightServiceZoneTop', label: 'Right top service mark', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'volleyballRightServiceZoneBottom', label: 'Right bottom service mark', group: 'center', side: 'center', role: 'line', fallback: 'lines' }
    ]
  },
  SHUFFLEBOARD: {
    groups: [
      { id: 'perimeter', label: 'Perimeter' },
      { id: 'left', label: 'Left scoring area' },
      { id: 'right', label: 'Right scoring area' }
    ],
    parts: [
      { id: 'shuffleLeftCourtArea', label: 'Playing area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleLeftTenOffTopArea', label: '10-off area (top)', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleLeftTenOffBottomArea', label: '10-off area (bottom)', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleLeftSevenTopArea', label: '7 area (top)', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleLeftSevenBottomArea', label: '7 area (bottom)', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleLeftEightTopArea', label: '8 area (top)', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleLeftEightBottomArea', label: '8 area (bottom)', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleLeftTenArea', label: '10 area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'shuffleSidelineTop', label: 'Top sideline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'shuffleSidelineBottom', label: 'Bottom sideline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'shuffleEndLineLeft', label: 'Left end line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'shuffleEndLineRight', label: 'Right end line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftBaseLine', label: 'Base line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftTenOffLine', label: '10-off line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftTriangleSideTop', label: 'Triangle top side', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftTriangleSideBottom', label: 'Triangle bottom side', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftSevenEightLine', label: '7/8 line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftEightTenLine', label: '8/10 line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftCenterLine', label: 'Center line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftTenOffTriangle', label: '10-off divider', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftDeadLine', label: 'Dead line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleLeftLabels', label: 'Scoring labels', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightCourtArea', label: 'Playing area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightTenOffTopArea', label: '10-off area (top)', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightTenOffBottomArea', label: '10-off area (bottom)', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightSevenTopArea', label: '7 area (top)', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightSevenBottomArea', label: '7 area (bottom)', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightEightTopArea', label: '8 area (top)', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightEightBottomArea', label: '8 area (bottom)', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightTenArea', label: '10 area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'shuffleRightBaseLine', label: 'Base line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightTenOffLine', label: '10-off line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightTriangleSideTop', label: 'Triangle top side', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightTriangleSideBottom', label: 'Triangle bottom side', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightSevenEightLine', label: '7/8 line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightEightTenLine', label: '8/10 line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightCenterLine', label: 'Center line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightTenOffTriangle', label: '10-off divider', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightDeadLine', label: 'Dead line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'shuffleRightLabels', label: 'Scoring labels', group: 'right', side: 'right', role: 'line', fallback: 'lines' }
    ]
  },
  CORNHOLE: {
    groups: [
      { id: 'perimeter', label: 'Perimeter' },
      { id: 'left', label: 'Left board' },
      { id: 'right', label: 'Right board' }
    ],
    parts: [
      { id: 'cornholeLeftCourtArea', label: 'Playing area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'cornholeLeftPitcherBoxUpperArea', label: 'Upper pitcher box area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'cornholeLeftPitcherBoxLowerArea', label: 'Lower pitcher box area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'cornholeLeftBoardArea', label: 'Board area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'cornholeSidelineTop', label: 'Top boundary', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'cornholeSidelineBottom', label: 'Bottom boundary', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'cornholeEndLineLeft', label: 'Left end boundary', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'cornholeEndLineRight', label: 'Right end boundary', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'cornholeLeftPitcherBoxUpper', label: 'Upper pitcher box', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'cornholeLeftPitcherBoxLower', label: 'Lower pitcher box', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'cornholeLeftBoardOutline', label: 'Board outline', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'cornholeLeftFoulLine', label: 'Foul line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'cornholeLeftBoardHole', label: 'Board hole', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'cornholeRightCourtArea', label: 'Playing area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'cornholeRightPitcherBoxUpperArea', label: 'Upper pitcher box area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'cornholeRightPitcherBoxLowerArea', label: 'Lower pitcher box area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'cornholeRightBoardArea', label: 'Board area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'cornholeRightPitcherBoxUpper', label: 'Upper pitcher box', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'cornholeRightPitcherBoxLower', label: 'Lower pitcher box', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'cornholeRightBoardOutline', label: 'Board outline', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'cornholeRightFoulLine', label: 'Foul line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'cornholeRightBoardHole', label: 'Board hole', group: 'right', side: 'right', role: 'line', fallback: 'lines' }
    ]
  },
  FUTSAL: {
    groups: [
      { id: 'borders', label: 'Borders' },
      { id: 'perimeter', label: 'Perimeter' },
      { id: 'center', label: 'Center' },
      { id: 'left', label: 'Left half' },
      { id: 'right', label: 'Right half' },
      { id: 'goals', label: 'Goals' }
    ],
    parts: [
      { id: 'futsalLeftHalf', label: 'Playing area', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'futsalRightHalf', label: 'Playing area', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'futsalLeftPenaltyArea', label: 'Penalty area', group: 'left', side: 'left', role: 'key', fallback: 'key' },
      { id: 'futsalRightPenaltyArea', label: 'Penalty area', group: 'right', side: 'right', role: 'key', fallback: 'key' },
      { id: 'futsalSidelineTop', label: 'Top touchline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalSidelineBottom', label: 'Bottom touchline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalGoalLineLeft', label: 'Left goal line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalGoalLineRight', label: 'Right goal line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalHalfwayLine', label: 'Halfway line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalCenterCircle', label: 'Center circle', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalLeftPenaltyAreaLine', label: 'Penalty area line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'futsalRightPenaltyAreaLine', label: 'Penalty area line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'futsalLeftPenaltyMark', label: 'Penalty mark', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'futsalRightPenaltyMark', label: 'Penalty mark', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'futsalLeftSecondPenaltyMark', label: 'Second penalty mark', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'futsalRightSecondPenaltyMark', label: 'Second penalty mark', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'futsalLeftSecondPenaltySideMarks', label: '10 m distance marks', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'futsalRightSecondPenaltySideMarks', label: '10 m distance marks', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'futsalLeftGoalkeeperDistanceMark', label: 'Goalkeeper distance mark', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'futsalRightGoalkeeperDistanceMark', label: 'Goalkeeper distance mark', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'futsalCornerArcs', label: 'Corner arcs', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalCornerDistanceMarks', label: 'Corner distance marks', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalSubstitutionMarks', label: 'Substitution zone marks', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'futsalGoalLeft', label: 'Left goal', group: 'goals', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'futsalGoalRight', label: 'Right goal', group: 'goals', side: 'right', role: 'line', fallback: 'lines' }
    ]
  },
  NETBALL: {
    groups: [
      { id: 'borders', label: 'Borders' },
      { id: 'perimeter', label: 'Perimeter' },
      { id: 'center', label: 'Court lines' },
      { id: 'left', label: 'Left third' },
      { id: 'middle', label: 'Center third' },
      { id: 'right', label: 'Right third' },
      { id: 'goals', label: 'Goals' }
    ],
    parts: [
      { id: 'netballLeftThird', label: 'Third', group: 'left', side: 'left', role: 'area', fallback: 'main' },
      { id: 'netballCenterThird', label: 'Third', group: 'middle', side: 'center', role: 'area', fallback: 'main' },
      { id: 'netballRightThird', label: 'Third', group: 'right', side: 'right', role: 'area', fallback: 'main' },
      { id: 'netballLeftGoalCircleArea', label: 'Goal circle', group: 'left', side: 'left', role: 'key', fallback: 'key' },
      { id: 'netballRightGoalCircleArea', label: 'Goal circle', group: 'right', side: 'right', role: 'key', fallback: 'key' },
      { id: 'netballSidelineTop', label: 'Top sideline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'netballSidelineBottom', label: 'Bottom sideline', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'netballGoalLineLeft', label: 'Left goal line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'netballGoalLineRight', label: 'Right goal line', group: 'perimeter', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'netballTransverseLineLeft', label: 'Left transverse line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'netballTransverseLineRight', label: 'Right transverse line', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'netballCenterCircle', label: 'Center circle', group: 'center', side: 'center', role: 'line', fallback: 'lines' },
      { id: 'netballLeftGoalCircleLine', label: 'Goal circle line', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'netballRightGoalCircleLine', label: 'Goal circle line', group: 'right', side: 'right', role: 'line', fallback: 'lines' },
      { id: 'netballGoalPostLeft', label: 'Goal post marker', group: 'left', side: 'left', role: 'line', fallback: 'lines' },
      { id: 'netballGoalPostRight', label: 'Goal post marker', group: 'right', side: 'right', role: 'line', fallback: 'lines' }
    ]
  }
};
// Non-basketball hoop overlay (tennis/pickleball/padel/volleyball/futsal/netball):
// We want border band fills around the (overlay) basketball court to be independently editable
// (left/right ends + top/bottom sides). These are painted via `state.detailColors.*` regardless
// of sport, but the per-sport detail libraries don't include them by default.
//
// We inject them into the active detail state for these sports so they appear in
// Colours → More Color Options and can be targeted by click-to-select.
const OVERLAY_BORDER_GROUP_ID = 'borders';
const OVERLAY_BORDER_GROUP_LABEL = 'Borders';
const OVERLAY_BORDER_PARTS = [
  { id: 'borderEndLeftFill', label: 'Left baseline border', group: OVERLAY_BORDER_GROUP_ID, side: 'left', role: 'area', fallback: 'border' },
  { id: 'borderEndRightFill', label: 'Right baseline border', group: OVERLAY_BORDER_GROUP_ID, side: 'right', role: 'area', fallback: 'border' },
  { id: 'borderSideTopFill', label: 'Top sideline border', group: OVERLAY_BORDER_GROUP_ID, side: 'center', role: 'area', fallback: 'border' },
  { id: 'borderSideBottomFill', label: 'Bottom sideline border', group: OVERLAY_BORDER_GROUP_ID, side: 'center', role: 'area', fallback: 'border' },
];
const OVERLAY_HOOP_GROUPS = [
  { id: 'overlay-left', label: 'Left hoop' },
  { id: 'overlay-right', label: 'Right hoop' },
  { id: 'overlay-top', label: 'Top hoop' },
  { id: 'overlay-bottom', label: 'Bottom hoop' }
];
const OVERLAY_BBALL_BORDER_LINE_PARTS = [
  { id: 'leftBorderLine', label: 'Sideline/baseline (left)', group: OVERLAY_BORDER_GROUP_ID, side: 'center', role: 'line', fallback: 'overlayLines' },
  { id: 'rightBorderLine', label: 'Sideline/baseline (right)', group: OVERLAY_BORDER_GROUP_ID, side: 'center', role: 'line', fallback: 'overlayLines' },
  { id: 'centerLine', label: 'Center line', group: OVERLAY_BORDER_GROUP_ID, side: 'center', role: 'line', fallback: 'overlayLines' },
  { id: 'centerCircleLine', label: 'Center circle', group: OVERLAY_BORDER_GROUP_ID, side: 'center', role: 'line', fallback: 'overlayLines' }
];
const OVERLAY_BBALL_HOOP_PARTS = [
  { id: 'leftThreePointArea', label: '3-point area', group: 'overlay-left', side: 'left', role: 'area', fallback: 'overlayMain' },
  { id: 'leftMidRangeArea', label: 'Mid-range area', group: 'overlay-left', side: 'left', role: 'area', fallback: 'overlayMain' },
  { id: 'leftKey', label: 'Key', group: 'overlay-left', side: 'left', role: 'key', fallback: 'overlayKey' },
  { id: 'leftThreePointLine', label: '3-point line', group: 'overlay-left', side: 'left', role: 'line', fallback: 'overlayLines' },
  { id: 'leftFreeThrowLines', label: 'Free throw line', group: 'overlay-left', side: 'left', role: 'line', fallback: 'overlayLines' },
  { id: 'leftFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'overlay-left', side: 'left', role: 'line', fallback: 'overlayLines' },
  { id: 'leftFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'overlay-left', side: 'left', role: 'line', fallback: 'overlayLines' },
  { id: 'leftRestrictedAreaLine', label: 'Restricted area', group: 'overlay-left', side: 'left', role: 'line', fallback: 'overlayLines' },
  { id: 'rightThreePointArea', label: '3-point area', group: 'overlay-right', side: 'right', role: 'area', fallback: 'overlayMain' },
  { id: 'rightMidRangeArea', label: 'Mid-range area', group: 'overlay-right', side: 'right', role: 'area', fallback: 'overlayMain' },
  { id: 'rightKey', label: 'Key', group: 'overlay-right', side: 'right', role: 'key', fallback: 'overlayKey' },
  { id: 'rightThreePointLine', label: '3-point line', group: 'overlay-right', side: 'right', role: 'line', fallback: 'overlayLines' },
  { id: 'rightFreeThrowLines', label: 'Free throw line', group: 'overlay-right', side: 'right', role: 'line', fallback: 'overlayLines' },
  { id: 'rightFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'overlay-right', side: 'right', role: 'line', fallback: 'overlayLines' },
  { id: 'rightFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'overlay-right', side: 'right', role: 'line', fallback: 'overlayLines' },
  { id: 'rightRestrictedAreaLine', label: 'Restricted area', group: 'overlay-right', side: 'right', role: 'line', fallback: 'overlayLines' },
  { id: 'topKey', label: 'Key', group: 'overlay-top', side: 'top', role: 'key', fallback: 'overlayKey' },
  { id: 'topThreePointLine', label: '3-point line', group: 'overlay-top', side: 'top', role: 'line', fallback: 'overlayLines' },
  { id: 'topFreeThrowLines', label: 'Free throw line', group: 'overlay-top', side: 'top', role: 'line', fallback: 'overlayLines' },
  { id: 'topFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'overlay-top', side: 'top', role: 'line', fallback: 'overlayLines' },
  { id: 'topFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'overlay-top', side: 'top', role: 'line', fallback: 'overlayLines' },
  { id: 'topRestrictedAreaLine', label: 'Restricted area', group: 'overlay-top', side: 'top', role: 'line', fallback: 'overlayLines' },
  { id: 'bottomKey', label: 'Key', group: 'overlay-bottom', side: 'bottom', role: 'key', fallback: 'overlayKey' },
  { id: 'bottomThreePointLine', label: '3-point line', group: 'overlay-bottom', side: 'bottom', role: 'line', fallback: 'overlayLines' },
  { id: 'bottomFreeThrowLines', label: 'Free throw line', group: 'overlay-bottom', side: 'bottom', role: 'line', fallback: 'overlayLines' },
  { id: 'bottomFreeThrowLaneLines', label: 'Free-throw lane lines', group: 'overlay-bottom', side: 'bottom', role: 'line', fallback: 'overlayLines' },
  { id: 'bottomFreeThrowHalfCircleLine', label: 'Free-throw half-circle', group: 'overlay-bottom', side: 'bottom', role: 'line', fallback: 'overlayLines' },
  { id: 'bottomRestrictedAreaLine', label: 'Restricted area', group: 'overlay-bottom', side: 'bottom', role: 'line', fallback: 'overlayLines' }
];

const BASKETBALL_BOUNDARY_LINE_DETAIL_ID = 'boundaryLine';
const BASKETBALL_OUTER_PERIMETER_LINE_DETAIL_ID = 'outerPerimeterLine';
const BASKETBALL_PERIMETER_DETAIL_IDS = [BASKETBALL_BOUNDARY_LINE_DETAIL_ID, BASKETBALL_OUTER_PERIMETER_LINE_DETAIL_ID];
const LEGACY_BASKETBALL_PERIMETER_DETAIL_IDS = ['leftBorderLine', 'rightBorderLine'];
const TILE_EDGE_DETAIL_IDS = Object.freeze({
  left: 'tileEdgeLeftFill',
  right: 'tileEdgeRightFill',
  top: 'tileEdgeTopFill',
  bottom: 'tileEdgeBottomFill'
});
const TILE_EDGE_SIDE_BITS = Object.freeze({ left: 1, right: 2, top: 4, bottom: 8 });
const TILE_EDGE_GROUP = Object.freeze({ id: 'edges', label: 'Edges' });
const TILE_EDGE_DETAIL_PARTS = Object.freeze([
  { id: TILE_EDGE_DETAIL_IDS.top, label: 'Top Edge', group: 'edges', side: 'top', role: 'area', fallback: 'edge' },
  { id: TILE_EDGE_DETAIL_IDS.bottom, label: 'Bottom Edge', group: 'edges', side: 'bottom', role: 'area', fallback: 'edge' },
  { id: TILE_EDGE_DETAIL_IDS.left, label: 'Left Edge', group: 'edges', side: 'left', role: 'area', fallback: 'edge' },
  { id: TILE_EDGE_DETAIL_IDS.right, label: 'Right Edge', group: 'edges', side: 'right', role: 'area', fallback: 'edge' }
]);

const DETAIL_SKIP = '__SKIP__';
function createDefaultExtraState() {
  const base = Object.create(null);
  BASE_EXTRA_LINE_TYPES.forEach(def => {
    base[def.id] = {
      enabled: false,
      color: def.defaultColor,
      scale: 1,
      rotation: 0,
      offsetX: 0,
      offsetY: 0
    };
  });
  return base;
}
function cloneExtraState(source) {
  const base = createDefaultExtraState();
  if (!source) return base;
  Object.keys(source).forEach(rawId => {
    const entry = source[rawId];
    if (!entry || typeof entry !== 'object') return;
    const id = normalizeExtraLineTypeId(rawId);
    const def = getExtraLineTypeDefinition(id) || ensureExtraLineTypeInstanceDefinition(id, entry.type || entry.instanceOf);
    if (!def) return;
    const next = {
      enabled: !!entry.enabled,
      color: entry.color || def.defaultColor,
      scale: Number.isFinite(entry.scale) ? entry.scale : 1,
      rotation: Number.isFinite(entry.rotation) ? entry.rotation : 0,
      offsetX: Number.isFinite(entry.offsetX) ? entry.offsetX : 0,
      offsetY: Number.isFinite(entry.offsetY) ? entry.offsetY : 0
    };
    if (def.instanceOf) {
      next.type = def.instanceOf;
      next.instance = def.instanceNumber;
    }
    base[def.id] = next;
  });
  return base;
}
// Allow small imperial presets (e.g., 10×16 ft) without clamping back to large values.
const LIMITS = {
  m:  { L: [0.01, 40],  W: [0.01, 40] },
  ft: { L: [0.03, 130], W: [0.03, 130] }
};
const SIZE_ADJUST_STEP_M = 0.1;
const LOGO_SCALE_MIN = 0.01;
const LOGO_SCALE_MAX = 2;
const MIN_LOGO_SIDE = 0.5; // meters (50 cm)
const LOGO_3D_TAP_MAX_PX = 24;
const LOGO_3D_TAP_MAX_MS = 800;
const UI_PPM_MAX = 140; // clamp px-per-meter in 2D UI canvas to avoid huge buffers
const TEX_PPM_MAX = 200;
const CANONICAL_PPM = 64;
const SNAPSHOT_EXPORT_MARGIN_PX = 40;
const HOOP_MARKER_RULES = new Set(['FIBA', 'NBA', 'NCAA', 'US_HIGHSCHOOL']);
// Non-basketball base courts that can optionally show basketball hoop hardware + hoop distance in 2D.
// This is an overlay feature: it must NOT change the underlying sport’s base line art.
const NON_BASKETBALL_HOOP_OVERLAY_RULES = new Set([
  'TENNIS',
  'PICKLEBALL',
  'PADEL',
  'VOLLEYBALL',
  'SHUFFLEBOARD',
  'CORNHOLE',
  'FUTSAL',
  'NETBALL'
]);
function buildCourtTypeCapabilities() {
  return Object.freeze(COURT_TYPES.reduce((acc, def) => {
    const id = normalizeCourtTypeId(def && def.id);
    const rulesetId = String(def && def.ruleset || '').trim().toUpperCase();
    const ruleset = RULESETS[rulesetId] || {};
    const isBasketball = !!(def && def.basketball);
    const supportsOverlayHoops = NON_BASKETBALL_HOOP_OVERLAY_RULES.has(rulesetId);
    const hasHoops = isBasketball || supportsOverlayHoops || !!ruleset.useHoops;
    const hoopModeKind = isBasketball
      ? 'basketball'
      : (supportsOverlayHoops ? 'overlay' : (hasHoops ? 'free' : 'none'));
    const allowedHoopModes = hoopModeKind === 'basketball'
      ? ['1', '2', '3', '4']
      : (hoopModeKind === 'none' ? ['0'] : ['0', '1', '2', '3', '4']);
    if (id && rulesetId) {
      acc[id] = Object.freeze({
        id: id,
        label: def.label || id,
        ruleset: rulesetId,
        basketball: isBasketball,
        usesHoops: hasHoops,
        hoopModeKind: hoopModeKind,
        allowedHoopModes: Object.freeze(allowedHoopModes)
      });
    }
    return acc;
  }, {}));
}
const COURT_TYPE_CAPABILITIES = buildCourtTypeCapabilities();
const HOOP_POLE_BORDER_M = 0.5;
const MULTI_BASKETBALL_TOUCH_HOOP_BORDER_M = 0.6096;
const SWISHCB_AUTO_HOOP_BORDER_LOCKS_KEY = '_swishcbAutoHoopBorderLocks';
const BORDER_LOCK_EPSILON_M = 1e-6;
const DEFAULT_HOOP_MODEL_PRESET = 'CV72';
// Hoop distance default (customer can override via the hoop-distance inputs).
// Keep the default overhang aligned with the default CV72 hoop model.
const DEFAULT_HOOP_OVERHANG_CM = 118.11;
const DEFAULT_HOOP_OVERHANG_M = DEFAULT_HOOP_OVERHANG_CM / 100;
// Extra pre-hide margin for the FIBA overlay center art on non-basketball courts.
// These courts are often shorter than 28m; with optional extra 3PT overlays, the center circle/line
// can visually “almost touch” before our one-line-width buffer triggers. Add a small fixed margin
// so the center art disappears sooner, matching expected preview behavior.
const NON_BASKETBALL_OVERLAY_CENTER_ART_HIDE_EXTRA_M = 0.20; // meters
const HOOP_MARKER_PATH = 'm340.16 1870.87v510.23m106.3-198.42c-31.35 0-56.69-25.34-56.69-56.69 0-31.35 25.34-56.69 56.69-56.69 31.35 0 56.69 25.34 56.69 56.69 0 31.35-25.34 56.69-56.69 56.69z';
const HOOP_MARKER_STROKE_UNITS = 14.173;
const HOOP_MARKER_UNITS_PER_M = 7937.008 / 28; // SVG viewBox units per meter (28 m wide design)
const HOOP_POLE_PATH = 'M50,2050.98 h150 v150 h-150 z'; // Pole anchor path from SVG
const HOOP_POLE_STROKE_UNITS = 14.173;
const HOOP_MARKER_BASE_WIDTH_M = 15; // marker paths were authored on a 15 m wide court (FIBA)
// Used as a fallback base for hoop-distance logic (including overlay mode).
// We intentionally use the global default instead of league-specific geometry.
const FIBA_BASE_OVERHANG_M = DEFAULT_HOOP_OVERHANG_M;

function createBasketballProceduralLineAPI(options = {}) {
  const SKIP_COLOR = '__SKIP__';
  const rulesetId = String(options.rulesetId || '').trim().toUpperCase();
  const official = Object.assign({}, cloneOfficialGeometry(rulesetId), options.geometry || {});
  const officialResolved = resolveOfficialCourtGeometry(rulesetId, {
    surfaceLengthM: Number(options.lengthM) || Number(official.playableLength) || undefined,
    surfaceWidthM: Number(options.widthM) || Number(official.playableWidth) || undefined,
    noSeries: true
  });
  const basketballFeature = officialResolved && officialResolved.features && officialResolved.features.basketball;
  const basketballDraw = (basketballFeature && basketballFeature.draw) || {};
  const court = {
    length: Math.max(0.001, Number(options.lengthM) || Number(official.playableLength) || 28),
    width: Math.max(0.001, Number(options.widthM) || Number(official.playableWidth) || 15),
    line: Math.max(0.001, Number(official.line) || 0.05),
    keyL: Math.max(0, Number(basketballDraw.keyLineXFromBaselineM) || (Math.max(0, (Number(official.keyL) || 0) - ((Number(official.line) || 0.05) * 0.5)))),
    keyFillL: Math.max(0, Number(basketballDraw.keyFillLengthM) || Number(official.keyL) || 0),
    keyW: Math.max(0, Number(basketballDraw.keyLineWidthM) || (Math.max(0, (Number(official.keyW) || 0) - (Number(official.line) || 0.05)))),
    keyFillW: Math.max(0, Number(basketballDraw.keyFillWidthM) || Number(official.keyW) || 0),
    ftR: Math.max(0, Number(basketballDraw.freeThrowCircleRStrokeCenterM) || Number(official.ftR) || 0),
    restrictedR: Math.max(0, Number(basketballDraw.restrictedAreaRStrokeCenterM) || Number(official.restrictedR) || 0),
    threeR: Math.max(0, Number(basketballDraw.threePointRStrokeCenterM) || Number(official.threeR) || 0),
    centerR: Math.max(0, Number(basketballDraw.centerCircleRStrokeCenterM) || Number(official.centerR) || 0),
    centerInnerR: Math.max(0, Number(basketballDraw.centerInnerCircleRStrokeCenterM) || Number(official.centerInnerR) || 0),
    centerLineExtension: Math.max(0, Number(basketballFeature && basketballFeature.official && basketballFeature.official.centerLineExtensionM) || Number(official.centerLineExtension) || 0),
    overhang: Math.max(0, Number(official.hoopToEnd) || 0),
    rimFromBackboard: Math.max(0, Number(official.rimFromBackboard) || 0.381),
    sideOffset: Number.isFinite(Number(basketballDraw.threePointSidelineStrokeCenterOffsetM))
      ? Math.max(0, Number(basketballDraw.threePointSidelineStrokeCenterOffsetM))
      : Number.isFinite(Number(official.threeSideOffset))
      ? Math.max(0, Number(official.threeSideOffset))
      : Math.max(0, ((Number(options.widthM) || Number(official.playableWidth) || 15) * 0.5) - (Number(official.threeR) || 0))
  };
  court.half = court.length * 0.5;
  court.hoopX = court.overhang + court.rimFromBackboard;
  court.keyTop = Number.isFinite(Number(basketballDraw.keyLineTopM))
    ? Math.max(0, Number(basketballDraw.keyLineTopM))
    : (court.width - court.keyW) * 0.5;
  court.keyBottom = Number.isFinite(Number(basketballDraw.keyLineBottomM))
    ? Math.min(court.width, Number(basketballDraw.keyLineBottomM))
    : court.keyTop + court.keyW;
  court.keyFillTop = Number.isFinite(Number(basketballDraw.keyFillTopM))
    ? Math.max(0, Number(basketballDraw.keyFillTopM))
    : (court.width - court.keyFillW) * 0.5;
  court.keyFillBottom = Number.isFinite(Number(basketballDraw.keyFillBottomM))
    ? Math.min(court.width, Number(basketballDraw.keyFillBottomM))
    : court.keyFillTop + court.keyFillW;
  let activeHoopDeltaM = 0;
  function activeDistance(baseM) {
    return Math.max(0, (Number(baseM) || 0) + (Number(activeHoopDeltaM) || 0));
  }
  function activeKeyLineM() {
    return activeDistance(court.keyL);
  }
  function activeKeyFillLengthM() {
    return activeDistance(court.keyFillL);
  }
  function activeHoopXM() {
    return activeDistance(court.hoopX);
  }
  function activeBackboardM() {
    return activeDistance(court.overhang);
  }
  function activeLaneMarkOffsetM(mark) {
    return Math.max(0, Number(mark && mark.offsetM) || 0);
  }

  function normHex(value) {
    if (!value) return '#FFFFFF';
    const raw = String(value).trim();
    return (raw.charAt(0) === '#' ? raw : ('#' + raw)).toUpperCase();
  }
  function hasKeys(obj) {
    if (!obj) return false;
    for (const _ in obj) return true;
    return false;
  }
  function makePart(id, order, role, side, drawFn) {
    return { id, order, role, side, draw: drawFn };
  }
  function computeDims(cw, ch) {
    const sx = cw / court.length;
    const sy = ch / court.width;
    return { sx, sy, sa: (sx + sy) * 0.5, cw, ch };
  }
  function surfaceFillStyle(ctx, color, dims) {
    const hardwood = swishcbConfigGlobal && swishcbConfigGlobal.SwishCBHardwood;
    if (!hardwood || typeof hardwood.getFillStyle !== 'function') return color;
    return hardwood.getFillStyle(ctx, color, {
      hardwood: dims && dims.hardwood,
      pixelsPerMeter: dims && dims.sa
    });
  }
  function getThreeArc() {
    const cy = court.width * 0.5;
    const yTop = Math.max(0, Math.min(court.width, court.sideOffset));
    const yBottom = Math.max(0, court.width - yTop);
    const dyTop = yTop - cy;
    const dyBottom = yBottom - cy;
    const r = Math.max(0.001, court.threeR);
    const xDeltaTop = Math.sqrt(Math.max(0, (r * r) - (dyTop * dyTop)));
    const xDeltaBottom = Math.sqrt(Math.max(0, (r * r) - (dyBottom * dyBottom)));
    return {
      cy,
      yTop,
      yBottom,
      thetaTop: Math.atan2(dyTop, xDeltaTop || 1e-9),
      thetaBottom: Math.atan2(dyBottom, xDeltaBottom || 1e-9),
      xArcTop: activeHoopXM() + xDeltaTop,
      xArcBottom: activeHoopXM() + xDeltaBottom
    };
  }
  function drawMirrored(ctx, x0, y0, cw, ch, fn, color, dims) {
    ctx.save();
    ctx.translate(x0 + cw, y0);
    ctx.scale(-1, 1);
    fn(ctx, 0, 0, cw, ch, color, dims);
    ctx.restore();
  }
  function drawThreePointArea(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.threeR > 0)) return;
    const a = getThreeArc();
    ctx.save();
    ctx.translate(x0, y0);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(court.half * dims.sx, 0);
    ctx.lineTo(court.half * dims.sx, court.width * dims.sy);
    ctx.lineTo(0, court.width * dims.sy);
    ctx.lineTo(0, a.yBottom * dims.sy);
    ctx.lineTo(a.xArcBottom * dims.sx, a.yBottom * dims.sy);
    ctx.ellipse(activeHoopXM() * dims.sx, a.cy * dims.sy, court.threeR * dims.sx, court.threeR * dims.sy, 0, a.thetaBottom, a.thetaTop, true);
    ctx.lineTo(0, a.yTop * dims.sy);
    ctx.closePath();
    ctx.fillStyle = surfaceFillStyle(ctx, color, dims);
    ctx.fill();
    ctx.restore();
  }
  function drawMidRangeArea(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.threeR > 0)) return;
    const a = getThreeArc();
    ctx.save();
    ctx.translate(x0, y0);
    ctx.beginPath();
    ctx.moveTo(0, a.yTop * dims.sy);
    ctx.lineTo(a.xArcTop * dims.sx, a.yTop * dims.sy);
    ctx.ellipse(activeHoopXM() * dims.sx, a.cy * dims.sy, court.threeR * dims.sx, court.threeR * dims.sy, 0, a.thetaTop, a.thetaBottom, false);
    ctx.lineTo(0, a.yBottom * dims.sy);
    ctx.lineTo(0, court.keyFillBottom * dims.sy);
    ctx.lineTo(activeKeyFillLengthM() * dims.sx, court.keyFillBottom * dims.sy);
    ctx.lineTo(activeKeyFillLengthM() * dims.sx, court.keyFillTop * dims.sy);
    ctx.lineTo(0, court.keyFillTop * dims.sy);
    ctx.closePath();
    ctx.fillStyle = surfaceFillStyle(ctx, color, dims);
    ctx.fill();
    ctx.restore();
  }
  function drawKeyArea(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.keyFillL > 0) || !(court.keyFillW > 0)) return;
    ctx.save();
    ctx.translate(x0, y0);
    ctx.fillStyle = surfaceFillStyle(ctx, color, dims);
    ctx.fillRect(0, court.keyFillTop * dims.sy, activeKeyFillLengthM() * dims.sx, court.keyFillW * dims.sy);
    ctx.restore();
  }
  function strokeSetup(ctx, color, dims) {
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, court.line * dims.sa);
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
    ctx.miterLimit = 10;
  }
  function drawThreePointLine(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.threeR > 0)) return;
    const a = getThreeArc();
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    ctx.beginPath();
    ctx.moveTo(0, a.yTop * dims.sy);
    ctx.lineTo(a.xArcTop * dims.sx, a.yTop * dims.sy);
    ctx.ellipse(activeHoopXM() * dims.sx, a.cy * dims.sy, court.threeR * dims.sx, court.threeR * dims.sy, 0, a.thetaTop, a.thetaBottom, false);
    ctx.lineTo(0, a.yBottom * dims.sy);
    ctx.stroke();
    ctx.restore();
  }
  function officialLaneMarks() {
    return Array.isArray(basketballDraw.laneMarks)
      ? basketballDraw.laneMarks.slice()
      : officialBasketballLaneMarks(rulesetId, court.line).slice();
  }
  const laneMarks = officialLaneMarks();
  const auxiliaryMarks = Array.isArray(basketballDraw.auxiliaryMarks)
    ? basketballDraw.auxiliaryMarks.slice()
    : officialBasketballAuxiliaryMarks(rulesetId, Object.assign({
      playableLength: court.length,
      playableWidth: court.width
    }, official), court.line).slice();
  const inboundMarks = auxiliaryMarks.filter((mark) => /SidelineHash|ThrowInLine/i.test(String(mark && mark.id || '')));
  const nonInboundAuxiliaryMarks = auxiliaryMarks.filter((mark) => !/SidelineHash|ThrowInLine/i.test(String(mark && mark.id || '')));
  function drawFreeThrowLines(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.keyL > 0) || !(court.keyW > 0)) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    const freeThrowOvershootM = Math.max(0, (Number(court.line) || 0) * 0.5);
    ctx.beginPath();
    ctx.moveTo(activeKeyLineM() * dims.sx, Math.max(0, court.keyFillTop - freeThrowOvershootM) * dims.sy);
    ctx.lineTo(activeKeyLineM() * dims.sx, Math.min(court.width, court.keyFillBottom + freeThrowOvershootM) * dims.sy);
    ctx.stroke();
    ctx.restore();
  }
  function drawFreeThrowLaneLines(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.keyL > 0) || !(court.keyW > 0)) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, court.keyTop * dims.sy);
    ctx.lineTo(activeKeyLineM() * dims.sx, court.keyTop * dims.sy);
    ctx.moveTo(0, court.keyBottom * dims.sy);
    ctx.lineTo(activeKeyLineM() * dims.sx, court.keyBottom * dims.sy);
    ctx.stroke();
    laneMarks.forEach((mark) => {
      const x = activeLaneMarkOffsetM(mark) * dims.sx;
      const len = Math.max(court.line * dims.sa, mark.lenM * dims.sa);
      ctx.save();
      ctx.lineWidth = Math.max(1, mark.widthM * dims.sa);
      ctx.beginPath();
      ctx.moveTo(x, court.keyTop * dims.sy);
      ctx.lineTo(x, (court.keyTop * dims.sy) - len);
      ctx.moveTo(x, court.keyBottom * dims.sy);
      ctx.lineTo(x, (court.keyBottom * dims.sy) + len);
      ctx.stroke();
      ctx.restore();
    });
    ctx.restore();
  }
  function drawFreeThrowHalfCircleLine(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.ftR > 0)) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    ctx.beginPath();
    ctx.ellipse(activeKeyLineM() * dims.sx, (court.width * 0.5) * dims.sy, court.ftR * dims.sx, court.ftR * dims.sy, 0, -Math.PI / 2, Math.PI / 2, false);
    ctx.stroke();
    ctx.restore();
  }
  function drawRestrictedPath(ctx, dims) {
    const cx = activeHoopXM() * dims.sx;
    const cy = (court.width * 0.5) * dims.sy;
    const rX = court.restrictedR * dims.sx;
    const rY = court.restrictedR * dims.sy;
    const backboardX = activeBackboardM() * dims.sx;
    if (rulesetId === 'FIBA' || rulesetId === 'NBA') {
      ctx.moveTo(backboardX, cy - rY);
      ctx.lineTo(cx, cy - rY);
      ctx.ellipse(cx, cy, rX, rY, 0, -Math.PI / 2, Math.PI / 2, false);
      ctx.lineTo(backboardX, cy + rY);
    } else {
      ctx.ellipse(cx, cy, rX, rY, 0, -Math.PI / 2, Math.PI / 2, false);
    }
  }
  function drawRestrictedArea(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.restrictedR > 0)) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    ctx.beginPath();
    drawRestrictedPath(ctx, dims);
    ctx.stroke();
    ctx.restore();
  }
  function drawBorder(ctx, x0, y0, cw, ch, color, dims) {
    if (!color) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, court.width * dims.sy);
    ctx.moveTo(0, 0);
    ctx.lineTo(court.half * dims.sx, 0);
    ctx.moveTo(0, court.width * dims.sy);
    ctx.lineTo(court.half * dims.sx, court.width * dims.sy);
    ctx.stroke();
    ctx.restore();
  }
  function drawInboundMarks(ctx, x0, y0, cw, ch, color, dims) {
    drawAuxiliaryLineMarks(ctx, x0, y0, cw, ch, color, dims, inboundMarks);
  }
  function drawOfficialAuxiliaryMarks(ctx, x0, y0, cw, ch, color, dims) {
    drawAuxiliaryLineMarks(ctx, x0, y0, cw, ch, color, dims, nonInboundAuxiliaryMarks);
  }
  function drawAuxiliaryLineMarks(ctx, x0, y0, cw, ch, color, dims, marks) {
    if (!color || !marks || !marks.length) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    marks.forEach((mark) => {
      if (!mark || mark.kind !== 'line') return;
      const x1 = Number(mark.x1M);
      const y1 = Number(mark.y1M);
      const x2 = Number(mark.x2M);
      const y2 = Number(mark.y2M);
      if (![x1, y1, x2, y2].every(Number.isFinite)) return;
      ctx.save();
      ctx.lineWidth = Math.max(1, (Number(mark.lineWidthM) || court.line) * dims.sa);
      ctx.beginPath();
      ctx.moveTo(x1 * dims.sx, y1 * dims.sy);
      ctx.lineTo(x2 * dims.sx, y2 * dims.sy);
      ctx.stroke();
      ctx.restore();
    });
    ctx.restore();
  }
  function drawCenterLine(ctx, x0, y0, cw, ch, color, dims) {
    if (!color) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    ctx.beginPath();
    ctx.moveTo(court.half * dims.sx, -court.centerLineExtension * dims.sy);
    ctx.lineTo(court.half * dims.sx, (court.width + court.centerLineExtension) * dims.sy);
    ctx.stroke();
    ctx.restore();
  }
  function drawCenterCircles(ctx, x0, y0, cw, ch, color, dims) {
    if (!color || !(court.centerR > 0)) return;
    ctx.save();
    ctx.translate(x0, y0);
    strokeSetup(ctx, color, dims);
    ctx.beginPath();
    ctx.ellipse(court.half * dims.sx, (court.width * 0.5) * dims.sy, court.centerR * dims.sx, court.centerR * dims.sy, 0, 0, Math.PI * 2);
    ctx.stroke();
    if (court.centerInnerR > 0) {
      ctx.beginPath();
      ctx.ellipse(court.half * dims.sx, (court.width * 0.5) * dims.sy, court.centerInnerR * dims.sx, court.centerInnerR * dims.sy, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  const parts = [
    makePart('leftThreePointArea', 10, 'area', 'left', drawThreePointArea),
    makePart('leftMidRangeArea', 20, 'area', 'left', drawMidRangeArea),
    makePart('leftKey', 30, 'key', 'left', drawKeyArea),
    makePart('leftThreePointLine', 90, 'line', 'left', drawThreePointLine),
    makePart('leftBorderLine', 100, 'line', 'left', drawBorder),
    makePart('leftInboundMarks', 105, 'line', 'left', drawInboundMarks),
    makePart('leftOfficialAuxiliaryMarks', 106, 'line', 'left', drawOfficialAuxiliaryMarks),
    makePart('leftFreeThrowLines', 110, 'line', 'left', drawFreeThrowLines),
    makePart('leftFreeThrowLaneLines', 112, 'line', 'left', drawFreeThrowLaneLines),
    makePart('leftFreeThrowHalfCircleLine', 115, 'line', 'left', drawFreeThrowHalfCircleLine),
    makePart('leftRestrictedAreaLine', 120, 'line', 'left', drawRestrictedArea),
    makePart('rightThreePointArea', 10, 'area', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawThreePointArea, color, dims)),
    makePart('rightMidRangeArea', 20, 'area', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawMidRangeArea, color, dims)),
    makePart('rightKey', 30, 'key', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawKeyArea, color, dims)),
    makePart('rightThreePointLine', 90, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawThreePointLine, color, dims)),
    makePart('rightBorderLine', 100, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawBorder, color, dims)),
    makePart('rightInboundMarks', 105, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawInboundMarks, color, dims)),
    makePart('rightOfficialAuxiliaryMarks', 106, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawOfficialAuxiliaryMarks, color, dims)),
    makePart('rightFreeThrowLines', 110, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawFreeThrowLines, color, dims)),
    makePart('rightFreeThrowLaneLines', 112, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawFreeThrowLaneLines, color, dims)),
    makePart('rightFreeThrowHalfCircleLine', 115, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawFreeThrowHalfCircleLine, color, dims)),
    makePart('rightRestrictedAreaLine', 120, 'line', 'right', (ctx, x0, y0, cw, ch, color, dims) => drawMirrored(ctx, x0, y0, cw, ch, drawRestrictedArea, color, dims)),
    makePart('centerLine', 150, 'line', 'center', drawCenterLine),
    makePart('centerCircleLine', 160, 'line', 'center', drawCenterCircles)
  ];
  const partLookup = parts.reduce((acc, part) => {
    acc[part.id] = part;
    return acc;
  }, Object.create(null));
  const drawOrder = parts.slice().sort((a, b) => a.order - b.order).map(part => part.id);
  const lineOnlyOrder = drawOrder.filter(id => partLookup[id] && partLookup[id].role === 'line');

  function shouldSkip(part, only, hideCenter, hideInbound) {
    if (!part) return true;
    if (hideCenter && part.side === 'center') return true;
    if (hideInbound && (part.id === 'leftInboundMarks' || part.id === 'rightInboundMarks')) return true;
    if (only === 'left' && part.side === 'right') return true;
    if (only === 'right' && part.side === 'left') return true;
    return false;
  }
  function pickColor(part, detailColors, fallbackColors, baseLineColor) {
    const direct = detailColors && detailColors[part.id];
    if (direct) return direct === SKIP_COLOR ? null : normHex(direct);
    const fallback = fallbackColors && fallbackColors[part.id];
    if (fallback) return fallback === SKIP_COLOR ? null : normHex(fallback);
    if (part.role === 'line') return baseLineColor;
    return null;
  }
  function resolveDrawBackboardM(opts, side) {
    const s = String(side || '').toLowerCase();
    const camel = s ? (s.charAt(0).toUpperCase() + s.slice(1)) : '';
    const candidates = [];
    if (opts && typeof opts === 'object') {
      candidates.push(opts['hoopOverhang' + camel + 'M']);
      candidates.push(opts[s + 'BackboardFromEndlineM']);
      if (opts.hoopOverhangs && typeof opts.hoopOverhangs === 'object') {
        candidates.push(opts.hoopOverhangs[s + 'M']);
        candidates.push(opts.hoopOverhangs[s]);
      }
      if (opts.hoopAnchors && typeof opts.hoopAnchors === 'object') {
        candidates.push(opts.hoopAnchors[s + 'M']);
        candidates.push(opts.hoopAnchors[s]);
        candidates.push(opts.hoopAnchors[s + 'BackboardFromEndlineM']);
      }
      candidates.push(opts.backboardFromEndlineM);
      candidates.push(opts.baseOverhangM);
    }
    for (let i = 0; i < candidates.length; i += 1) {
      const n = Number(candidates[i]);
      if (Number.isFinite(n)) return Math.max(0, n);
    }
    return court.overhang;
  }
  function draw(ctx, x0, y0, cw, ch, color, opts) {
    if (!ctx || !cw || !ch) return;
    const only = (opts && (opts.only === 'left' || opts.only === 'right')) ? opts.only : null;
    const hideCenter = !!(opts && opts.hideCenter);
    const hideInbound = !!(opts && opts.hideInboundMarks);
    const detailColors = (opts && opts.detailColors) || null;
    const fallbackColors = (opts && opts.fallbackColors) || null;
    const useDetailedParts = !!(opts && opts.useDetailedParts);
    const custom = hasKeys(detailColors) || hasKeys(fallbackColors);
    const order = useDetailedParts || custom ? drawOrder : lineOnlyOrder;
    const lineColor = normHex(color || '#FFFFFF');
    const dims = computeDims(cw, ch);
    dims.hardwood = opts && opts.hardwood;
    const deltaBySide = {
      left: resolveDrawBackboardM(opts, 'left') - court.overhang,
      right: resolveDrawBackboardM(opts, 'right') - court.overhang
    };
    for (const id of order) {
      const part = partLookup[id];
      if (shouldSkip(part, only, hideCenter, hideInbound)) continue;
      const fill = pickColor(part, detailColors, fallbackColors, lineColor);
      if (!fill) continue;
      const isFixedToBoundary = id === 'leftBorderLine' || id === 'rightBorderLine' || id === 'leftInboundMarks' || id === 'rightInboundMarks';
      const previousDelta = activeHoopDeltaM;
      activeHoopDeltaM = (!isFixedToBoundary && (part.side === 'left' || part.side === 'right'))
        ? (Number(deltaBySide[part.side]) || 0)
        : 0;
      try {
        part.draw(ctx, x0, y0, cw, ch, fill, dims);
      } finally {
        activeHoopDeltaM = previousDelta;
      }
    }
  }

  return {
    ready: Promise.resolve(),
    draw,
    design: {
      W: court.width * 1000,
      L: court.length * 1000
    },
    officialGeometry: official
  };
}

function resolveBasketballRuleGeometry(rulesOrId, options = {}) {
  const rawId = (typeof rulesOrId === 'string') ? String(rulesOrId || '').trim().toUpperCase() : '';
  const mappedRulesetId = (typeof rulesOrId === 'string')
    ? String(COURT_TYPE_TO_RULESET[normalizeCourtTypeId(rulesOrId)] || '').trim().toUpperCase()
    : '';
  const rules = rawId ? (RULESETS[rawId] || RULESETS[mappedRulesetId]) : rulesOrId;
  const rulesetId = String(rules && rules.id || rawId || mappedRulesetId || '').trim().toUpperCase();
  const geom = rules && rules.geometry ? rules.geometry : null;
  if (!rules || !geom || !BASKETBALL_RULESETS.has(rulesetId)) return null;

  const lineM = Math.max(0, Number(geom.line) || 0);
  const officialBackboardFromBaselineM = Math.max(0, Number(geom.hoopToEnd) || 0);
  const requestedBackboard = Number.isFinite(Number(options.backboardFromBaselineM))
    ? Number(options.backboardFromBaselineM)
    : (Number.isFinite(Number(options.effectiveBackboardFromBaselineM))
      ? Number(options.effectiveBackboardFromBaselineM)
      : officialBackboardFromBaselineM);
  const hardwareBackboardFromBaselineM = Math.max(0, requestedBackboard);
  const effectiveBackboardFromBaselineM = hardwareBackboardFromBaselineM;
  const overhangShiftTowardBaselineM = officialBackboardFromBaselineM - effectiveBackboardFromBaselineM;
  const rimCenterFromBackboardM = Math.max(0, Number(geom.rimFromBackboard) || 0);
  const keyDepthFarEdgeM = Math.max(0, Number(geom.keyL) || 0);
  const keyWidthM = Math.max(0, Number(geom.keyW) || 0);
  const freeThrowFarEdgeFromBackboardM = Math.max(0, keyDepthFarEdgeM - officialBackboardFromBaselineM);
  const freeThrowStrokeCenterFromBackboardM = Math.max(0, freeThrowFarEdgeFromBackboardM - (lineM * 0.5));
  const activeFreeThrowFarEdgeFromBaselineM = effectiveBackboardFromBaselineM + freeThrowFarEdgeFromBackboardM;
  const freeThrowStrokeCenterFromBaselineM = Math.max(0, effectiveBackboardFromBaselineM + freeThrowStrokeCenterFromBackboardM);
  const overhangAdjustedFreeThrowFarEdgeFromBaselineM = activeFreeThrowFarEdgeFromBaselineM;
  const overhangAdjustedStrokeCenterFromBaselineM = freeThrowStrokeCenterFromBaselineM;

  return Object.freeze({
    rulesetId: rulesetId,
    lineM: lineM,
    officialLineWidthM: lineM,
    officialBackboardFromBaselineM: officialBackboardFromBaselineM,
    backboardFromBaselineM: effectiveBackboardFromBaselineM,
    backboardOffsetM: officialBackboardFromBaselineM,
    effectiveBackboardFromBaselineM: effectiveBackboardFromBaselineM,
    hardwareBackboardFromBaselineM: hardwareBackboardFromBaselineM,
    hardwareOverhangDeltaFromOfficialM: hardwareBackboardFromBaselineM - officialBackboardFromBaselineM,
    overhangShiftTowardBaselineM: overhangShiftTowardBaselineM,
    rimCenterFromBackboardM: rimCenterFromBackboardM,
    rimCenterFromBaselineM: effectiveBackboardFromBaselineM + rimCenterFromBackboardM,
    officialRimCenterFromBaselineM: officialBackboardFromBaselineM + rimCenterFromBackboardM,
    effectiveRimCenterFromBaselineM: effectiveBackboardFromBaselineM + rimCenterFromBackboardM,
    freeThrowFarEdgeFromBackboardM: freeThrowFarEdgeFromBackboardM,
    officialFreeThrowFarEdgeFromBaselineM: keyDepthFarEdgeM,
    freeThrowFarEdgeFromBaselineM: activeFreeThrowFarEdgeFromBaselineM,
    freeThrowStrokeCenterFromBackboardM: freeThrowStrokeCenterFromBackboardM,
    freeThrowStrokeCenterFromBaselineM: freeThrowStrokeCenterFromBaselineM,
    overhangAdjustedFreeThrowFarEdgeFromBaselineM: overhangAdjustedFreeThrowFarEdgeFromBaselineM,
    overhangAdjustedStrokeCenterFromBaselineM: overhangAdjustedStrokeCenterFromBaselineM,
    keyDepthFarEdgeM: activeFreeThrowFarEdgeFromBaselineM,
    officialKeyDepthFarEdgeM: keyDepthFarEdgeM,
    keyDepthM: activeFreeThrowFarEdgeFromBaselineM,
    baselineKeyDepthM: keyDepthFarEdgeM,
    keyFillDepthM: activeFreeThrowFarEdgeFromBaselineM,
    keyWidthM: keyWidthM,
    keyFillWidthM: keyWidthM
  });
}

function resolveBasketballFreeThrowStrokeRadiusForKeyWidth(keyWidthM, options = {}) {
  const keyWidth = Math.max(0, Number(keyWidthM) || 0);
  const officialKeyWidth = Math.max(
    0,
    Number(options.officialKeyWidthM) ||
      Number(options.officialKeyW) ||
      Number(options.officialWidthM) ||
      0
  );
  const lineM = Math.max(0, Number(options.lineM) || Number(options.lineWidthM) || 0);
  const minRadiusM = Math.max(0, lineM * 0.5);
  let officialRadiusM = Number(options.officialFreeThrowStrokeRadiusM);
  if (!Number.isFinite(officialRadiusM) || officialRadiusM <= 0) {
    const outerRadiusM = Number(options.officialFreeThrowOuterRadiusM);
    if (Number.isFinite(outerRadiusM) && outerRadiusM > 0) {
      officialRadiusM = Math.max(0, outerRadiusM - minRadiusM);
    }
  }
  if (!Number.isFinite(officialRadiusM) || officialRadiusM <= 0) {
    officialRadiusM = Number(options.officialFreeThrowRadiusM);
  }
  if (!Number.isFinite(officialRadiusM) || officialRadiusM <= 0) return minRadiusM;
  if (keyWidth > 0 && officialKeyWidth > 0) {
    return Math.max(minRadiusM, keyWidth * (officialRadiusM / officialKeyWidth));
  }
  return Math.max(minRadiusM, officialRadiusM);
}

  try {
    swishcbConfigGlobal.SwishCBConfig = Object.assign({}, swishcbConfigGlobal.SwishCBConfig || {}, {
      RULESETS: RULESETS,
      OFFICIAL_GEOMETRY: OFFICIAL_GEOMETRY,
      OFFICIAL_SOURCE_URLS: OFFICIAL_SOURCE_URLS,
      HOOP_PRESET_SOURCE_URLS: HOOP_PRESET_SOURCE_URLS,
      HOOP_PRESET_SPECS: HOOP_PRESET_SPECS,
      normalizeHoopPresetId: normalizeHoopPresetId,
      cloneHoopPresetSpec: cloneHoopPresetSpec,
      resolveHoopPresetGeometry: resolveHoopPresetGeometry,
      cloneOfficialGeometry: cloneOfficialGeometry,
      resolveOfficialCourtGeometry: resolveOfficialCourtGeometry,
      resolveBasketballRuleGeometry: resolveBasketballRuleGeometry,
      resolveOfficialBasketballLaneMarks: resolveOfficialBasketballLaneMarks,
      resolveBasketballFreeThrowStrokeRadiusForKeyWidth: resolveBasketballFreeThrowStrokeRadiusForKeyWidth,
      createBasketballProceduralLineAPI: createBasketballProceduralLineAPI,
      COURT_TYPES: COURT_TYPES,
      COURT_BUILDER_LINKS: COURT_BUILDER_LINKS,
      COURT_BUILDER_ROUTES: COURT_BUILDER_ROUTES,
      COURT_TYPE_ALIASES: COURT_TYPE_ALIASES,
      normalizeCourtTypeId: normalizeCourtTypeId,
      COURT_TYPE_TO_RULESET: COURT_TYPE_TO_RULESET,
      RULESET_TO_COURT_TYPE: RULESET_TO_COURT_TYPE,
      DEFAULT_COURT_TYPE: DEFAULT_COURT_TYPE,
      BASKETBALL_RULESETS: BASKETBALL_RULESETS,
      NON_BASKETBALL_UI_LOCK_COURT_TYPES: NON_BASKETBALL_UI_LOCK_COURT_TYPES,
      BASKETBALL_EXTRA_LINE_IDS: BASKETBALL_EXTRA_LINE_IDS,
      COURT_TYPE_CAPABILITIES: COURT_TYPE_CAPABILITIES,
      EXTRA_LINE_TYPES: EXTRA_LINE_TYPES,
      REPEATABLE_EXTRA_SPORT_IDS: REPEATABLE_EXTRA_SPORT_IDS,
      getExtraLineTypeDefinition: getExtraLineTypeDefinition,
      ensureExtraLineTypeInstanceDefinition: ensureExtraLineTypeInstanceDefinition,
      removeExtraLineTypeInstanceDefinition: removeExtraLineTypeInstanceDefinition,
      getExtraLineTypeBaseId: getExtraLineTypeBaseId,
      EXTRA_SCALE_MIN: EXTRA_SCALE_MIN,
      EXTRA_SCALE_MAX: EXTRA_SCALE_MAX,
      EXTRA_ROT_MAX: EXTRA_ROT_MAX,
      THREE_MEASURE_DEDUP_EPS_M: THREE_MEASURE_DEDUP_EPS_M,
      THREE_MEASURE_ROT_DEG_BY_RULESET: THREE_MEASURE_ROT_DEG_BY_RULESET,
      THREE_MEASURE_ALIGN_RULESETS: THREE_MEASURE_ALIGN_RULESETS,
      DEFAULT_RULESET: DEFAULT_RULESET,
      FUTSAL_STANDARD_PLAYING_LENGTH_M: FUTSAL_STANDARD_PLAYING_LENGTH_M,
      FUTSAL_STANDARD_PLAYING_WIDTH_M: FUTSAL_STANDARD_PLAYING_WIDTH_M,
      FUTSAL_STANDARD_MIN_LENGTH_M: FUTSAL_STANDARD_MIN_LENGTH_M,
      FUTSAL_STANDARD_MIN_WIDTH_M: FUTSAL_STANDARD_MIN_WIDTH_M,
      FUTSAL_DEFAULT_PENALTY_WIDTH_M: FUTSAL_DEFAULT_PENALTY_WIDTH_M,
      FUTSAL_DEFAULT_PENALTY_LENGTH_M: FUTSAL_DEFAULT_PENALTY_LENGTH_M,
      NETBALL_OFFICIAL_PLAYING_LENGTH_M: NETBALL_OFFICIAL_PLAYING_LENGTH_M,
      NETBALL_OFFICIAL_PLAYING_WIDTH_M: NETBALL_OFFICIAL_PLAYING_WIDTH_M,
      NETBALL_DEFAULT_GOAL_CIRCLE_RADIUS_M: NETBALL_DEFAULT_GOAL_CIRCLE_RADIUS_M,
      NETBALL_CENTER_CIRCLE_RADIUS_M: NETBALL_CENTER_CIRCLE_RADIUS_M,
      DETAIL_LIBRARY: DETAIL_LIBRARY,
      OVERLAY_BORDER_GROUP_ID: OVERLAY_BORDER_GROUP_ID,
      OVERLAY_BORDER_GROUP_LABEL: OVERLAY_BORDER_GROUP_LABEL,
      OVERLAY_BORDER_PARTS: OVERLAY_BORDER_PARTS,
      OVERLAY_HOOP_GROUPS: OVERLAY_HOOP_GROUPS,
      OVERLAY_BBALL_BORDER_LINE_PARTS: OVERLAY_BBALL_BORDER_LINE_PARTS,
      OVERLAY_BBALL_HOOP_PARTS: OVERLAY_BBALL_HOOP_PARTS,
      BASKETBALL_BOUNDARY_LINE_DETAIL_ID: BASKETBALL_BOUNDARY_LINE_DETAIL_ID,
      BASKETBALL_OUTER_PERIMETER_LINE_DETAIL_ID: BASKETBALL_OUTER_PERIMETER_LINE_DETAIL_ID,
      BASKETBALL_PERIMETER_DETAIL_IDS: BASKETBALL_PERIMETER_DETAIL_IDS,
      LEGACY_BASKETBALL_PERIMETER_DETAIL_IDS: LEGACY_BASKETBALL_PERIMETER_DETAIL_IDS,
      TILE_EDGE_DETAIL_IDS: TILE_EDGE_DETAIL_IDS,
      TILE_EDGE_SIDE_BITS: TILE_EDGE_SIDE_BITS,
      TILE_EDGE_GROUP: TILE_EDGE_GROUP,
      TILE_EDGE_DETAIL_PARTS: TILE_EDGE_DETAIL_PARTS,
      DETAIL_SKIP: DETAIL_SKIP,
      createDefaultExtraState: createDefaultExtraState,
      cloneExtraState: cloneExtraState,
      LIMITS: LIMITS,
      SIZE_ADJUST_STEP_M: SIZE_ADJUST_STEP_M,
      LOGO_SCALE_MIN: LOGO_SCALE_MIN,
      LOGO_SCALE_MAX: LOGO_SCALE_MAX,
      MIN_LOGO_SIDE: MIN_LOGO_SIDE,
      LOGO_3D_TAP_MAX_PX: LOGO_3D_TAP_MAX_PX,
      LOGO_3D_TAP_MAX_MS: LOGO_3D_TAP_MAX_MS,
      UI_PPM_MAX: UI_PPM_MAX,
      TEX_PPM_MAX: TEX_PPM_MAX,
      CANONICAL_PPM: CANONICAL_PPM,
      SNAPSHOT_EXPORT_MARGIN_PX: SNAPSHOT_EXPORT_MARGIN_PX,
      HOOP_MARKER_RULES: HOOP_MARKER_RULES,
      NON_BASKETBALL_HOOP_OVERLAY_RULES: NON_BASKETBALL_HOOP_OVERLAY_RULES,
      HOOP_POLE_BORDER_M: HOOP_POLE_BORDER_M,
      MULTI_BASKETBALL_TOUCH_HOOP_BORDER_M: MULTI_BASKETBALL_TOUCH_HOOP_BORDER_M,
      SWISHCB_AUTO_HOOP_BORDER_LOCKS_KEY: SWISHCB_AUTO_HOOP_BORDER_LOCKS_KEY,
      BORDER_LOCK_EPSILON_M: BORDER_LOCK_EPSILON_M,
      DEFAULT_HOOP_MODEL_PRESET: DEFAULT_HOOP_MODEL_PRESET,
      DEFAULT_HOOP_OVERHANG_CM: DEFAULT_HOOP_OVERHANG_CM,
      DEFAULT_HOOP_OVERHANG_M: DEFAULT_HOOP_OVERHANG_M,
      NON_BASKETBALL_OVERLAY_CENTER_ART_HIDE_EXTRA_M: NON_BASKETBALL_OVERLAY_CENTER_ART_HIDE_EXTRA_M,
      HOOP_MARKER_PATH: HOOP_MARKER_PATH,
      HOOP_MARKER_STROKE_UNITS: HOOP_MARKER_STROKE_UNITS,
      HOOP_MARKER_UNITS_PER_M: HOOP_MARKER_UNITS_PER_M,
      HOOP_POLE_PATH: HOOP_POLE_PATH,
      HOOP_POLE_STROKE_UNITS: HOOP_POLE_STROKE_UNITS,
      HOOP_MARKER_BASE_WIDTH_M: HOOP_MARKER_BASE_WIDTH_M,
      FIBA_BASE_OVERHANG_M: FIBA_BASE_OVERHANG_M,
      resolveBasketballGeometry: resolveBasketballRuleGeometry
    });
    swishcbConfigGlobal.__swishcbConfigScriptReady = true;
  } catch (e) {}
})();
